import './InternalMobile.css';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import API_BASE_URL from './config/api';
import Spinner from 'react-bootstrap/Spinner';
import { FaBuilding, FaExclamationTriangle, FaCalendarAlt, FaCheckCircle, FaClock, FaChevronDown, FaChevronRight, FaList, FaTimes, FaChevronLeft, FaArrowLeft, FaPlus, FaFileContract, FaLock } from 'react-icons/fa';
import { canSeeBillingDetails, readMaintenanceUser } from './maintenanceAccess';
import { JobberClientPicker } from './JobberBillingFields';
import { contractScopeLabel, contractScopeNeedsReview, maintenanceScopeLabel, scopeContract } from './utils/maintenanceScope';

// Parse date-only (YYYY-MM-DD) as local date to avoid UTC midnight shifting to previous day
function parseLocalDate(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(dateStr);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = parseLocalDate(dateStr);
    if (!d || isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatShortDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = parseLocalDate(dateStr);
    if (!d || isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatShortDateWithYear(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = parseLocalDate(dateStr);
    if (!d || isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function getFrequencyLabel(cyclesPerYear) {
  const v = Number(cyclesPerYear);
  if (!Number.isFinite(v) || v <= 0) return '';
  if (v >= 1) return `${Math.round(v)}x / yr`;
  // Fractional values represent "1 cycle every N years".
  const years = Math.round(1 / v);
  if (!years || !Number.isFinite(years)) return `${v}x / yr`;
  return years === 1 ? '1x / yr' : `1x / ${years}yr`;
}

function getContractTypeLabel(type) {
  if (!type) return 'Maintenance';
  switch (type.toLowerCase()) {
    case 'hvac-maintenance':
      return 'HVAC';
    case 'dryer-vent':
      return 'Dryer Vent';
    case 'mechanical-room':
      return 'Mech Room';
    default:
      return type;
  }
}

function getContractTypeColor(type) {
  switch ((type || '').toLowerCase()) {
    case 'hvac-maintenance':
      return { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' };
    case 'dryer-vent':
      return { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' };
    default:
      return { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
  }
}

// Contract row component - shows lifetime and current period cycles
function ContractRow({ building, showHistory = false, onToggleHistory, onSchedule }) {
  const { 
    scheduleStatus, 
    upcomingInspection, 
    maintenanceHistory, 
    lastMaintenanceDate, 
    daysSinceLastMaintenance,
    contractType,
    cyclesPerYear,
    expiryDate,
    daysUntilExpiry,
    isExpiringSoon,
    lifetimeCyclesDone,
    lifetimeCyclesTotal,
    cyclesLast12Months,
    expectedAnnualCycles,
    neverDone,
    nextDueDate,
    daysUntilNextDue,
    isOverdue,
    recommendedNextPeriod,
    recommendedNextWindow,
    cyclesRemaining,
    termMonths,
  } = building;

  const typeColors = getContractTypeColor(contractType);
  const contractComplete = scheduleStatus === 'contract_complete' || Boolean(building.contractObligationComplete);
  
  // Format term months to readable format
  const getTermLabel = (months) => {
    if (!months) return '—';
    const years = months / 12;
    if (years === 1) return '1 Year';
    if (years === 2) return '2 Year';
    if (years === 3) return '3 Year';
    if (years === 4) return '4 Year';
    if (years === 5) return '5 Year';
    if (Number.isInteger(years)) return `${years} Year`;
    return `${months}mo`;
  };
  const currentPeriodComplete = cyclesLast12Months >= expectedAnnualCycles;
  
  // Format days since as readable text
  const getWaitTime = (days) => {
    if (!days) return '';
    if (days < 30) return `${days}d`;
    if (days < 60) return '1mo';
    return `${Math.floor(days / 30)}mo`;
  };

  return (
    <div
      style={{
        backgroundColor: scheduleStatus === 'done' || contractComplete ? '#f0fdf4' : scheduleStatus === 'needs_scheduling' ? '#fef2f2' : '#ffffff',
        borderRadius: '10px',
        marginBottom: '0.5rem',
        overflow: 'hidden',
        border: scheduleStatus === 'done' || contractComplete
          ? '2px solid #86efac'
          : scheduleStatus === 'needs_scheduling' 
            ? '2px solid #fca5a5' 
            : scheduleStatus === 'upcoming' 
              ? '2px solid #93c5fd'
              : '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}
    >
      <div className="schedule-contract-row"
        style={{
          padding: '0.65rem 1rem',
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 2fr) 80px 100px 100px 130px auto',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        {/* Building Name + Contract Type Badge + Meta */}
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.15rem' }}>
            <span
              style={{
                fontWeight: 600,
                fontSize: '0.9rem',
                color: '#111827',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {building.name}
            </span>
            <span
              style={{
                padding: '0.1rem 0.35rem',
                borderRadius: '4px',
                fontSize: '0.6rem',
                fontWeight: 600,
                backgroundColor: typeColors.bg,
                color: typeColors.text,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                maxWidth: '100%',
              }}
            >
              {contractScopeLabel(building, getContractTypeLabel(contractType))}
            </span>
            {isExpiringSoon && (
              <span
                style={{
                  padding: '0.1rem 0.35rem',
                  borderRadius: '4px',
                  fontSize: '0.5rem',
                  fontWeight: 700,
                  backgroundColor: daysUntilExpiry < 0 ? '#dc2626' : '#f59e0b',
                  color: '#ffffff',
                  whiteSpace: 'nowrap',
                }}
              >
                {contractComplete
                  ? (daysUntilExpiry < 0 ? 'EXPIRED — RENEW' : 'RENEWAL DUE')
                  : (daysUntilExpiry < 0 ? 'EXPIRED - LAST MAINT' : 'EXPIRES AFTER MAINT')}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.65rem', color: '#6b7280', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>{getFrequencyLabel(cyclesPerYear)}</span>
            {building.units && <span>• {building.units} units</span>}
            {lastMaintenanceDate ? (
              <span 
                style={{ 
                  color: daysSinceLastMaintenance > 180 ? '#f59e0b' : '#059669',
                  fontWeight: scheduleStatus === 'needs_scheduling' ? 600 : 500,
                }}
              >
                • Last: {formatShortDateWithYear(lastMaintenanceDate)} ({getWaitTime(daysSinceLastMaintenance)} ago)
              </span>
            ) : (
              <span style={{ color: '#dc2626', fontWeight: 600, backgroundColor: '#fef2f2', padding: '0.1rem 0.3rem', borderRadius: '4px' }}>
                • Never done
              </span>
            )}
          </div>
        </div>

        {/* Term */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '0.95rem', 
            fontWeight: 700, 
            color: '#7c3aed',
          }}>
            {getTermLabel(termMonths)}
          </div>
          {expiryDate && (
            <div style={{ fontSize: '0.55rem', color: isExpiringSoon ? '#f59e0b' : '#6b7280' }}>
              Exp: {formatShortDateWithYear(expiryDate)}
            </div>
          )}
        </div>

        {/* Lifetime Cycles */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.55rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.1rem' }}>
            Contract
          </div>
          <div style={{ 
            fontSize: '1.1rem', 
            fontWeight: 700, 
            color: lifetimeCyclesTotal && lifetimeCyclesDone >= lifetimeCyclesTotal ? '#7c3aed' : '#374151',
          }}>
            {lifetimeCyclesDone}/{lifetimeCyclesTotal || '?'}
          </div>
          <div style={{ fontSize: '0.55rem', color: '#6b7280' }}>
            cycles done
          </div>
        </div>

        {/* Last 12 Months Cycles - THE KEY METRIC */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.55rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.1rem' }}>
            Last 12 Mo
          </div>
          <div style={{ 
            fontSize: '1.1rem', 
            fontWeight: 700, 
            color: currentPeriodComplete || contractComplete ? '#16a34a' : '#dc2626',
          }}>
            {cyclesLast12Months}/{expectedAnnualCycles}
          </div>
          <div style={{ 
            fontSize: '0.55rem', 
            color: currentPeriodComplete || contractComplete ? '#16a34a' : '#dc2626',
            fontWeight: 500 
          }}>
            {contractComplete
              ? '✓ fulfilled'
              : currentPeriodComplete
                ? '✓ current'
                : `${Math.max(0, expectedAnnualCycles - cyclesLast12Months)} needed`}
          </div>
        </div>

        {/* Status Badge */}
        <div style={{ textAlign: 'center' }}>
          {scheduleStatus === 'needs_scheduling' && (
            <button
              onClick={() => onSchedule && onSchedule(building)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.4rem 0.75rem',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 700,
                backgroundColor: '#dc2626',
                color: '#ffffff',
                whiteSpace: 'nowrap',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
            >
              <FaExclamationTriangle style={{ fontSize: '0.65rem' }} />
              SCHEDULE
            </button>
          )}

          {scheduleStatus === 'upcoming' && upcomingInspection && (
            <button
              onClick={() => onSchedule && onSchedule(building)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.45rem 0.85rem',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: '#2563eb',
                color: '#ffffff',
                whiteSpace: 'nowrap',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                minWidth: 'fit-content',
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
              title="Click to edit"
            >
              <FaClock style={{ fontSize: '0.6rem', flexShrink: 0 }} />
              {upcomingInspection.endTerm && upcomingInspection.endTerm !== upcomingInspection.startTerm
                ? `${formatShortDate(upcomingInspection.startTerm)} – ${formatShortDate(upcomingInspection.endTerm)}`
                : formatShortDate(upcomingInspection.startTerm)}
            </button>
          )}

          {(scheduleStatus === 'done' || contractComplete) && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor: '#16a34a',
                  color: '#ffffff',
                  whiteSpace: 'nowrap',
                }}
              >
                <FaCheckCircle style={{ fontSize: '0.6rem' }} />
                {contractComplete ? 'CONTRACT COMPLETE' : 'UP TO DATE'}
              </div>
              {!contractComplete && recommendedNextWindow && lifetimeCyclesDone < lifetimeCyclesTotal && (
                <div style={{ 
                  fontSize: '0.6rem', 
                  color: '#7c3aed', 
                  fontWeight: 500,
                  textAlign: 'center',
                }}>
                  Next: {recommendedNextWindow}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Expand/Collapse Button for History */}
        <div style={{ textAlign: 'right' }}>
          {((maintenanceHistory && maintenanceHistory.length > 0) || scheduleStatus === 'done' || contractComplete) && (
            <button
              onClick={() => onToggleHistory(`${building.buildingId}-${contractType}`)}
              style={{
                background: 'none',
                border: 'none',
                padding: '0.4rem',
                cursor: 'pointer',
                color: '#9ca3af',
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.8rem',
              }}
              title="View history"
            >
              {showHistory ? <FaChevronDown /> : <FaChevronRight />}
            </button>
          )}
        </div>
      </div>

      {/* History Section (when expanded) */}
      {showHistory && ((maintenanceHistory && maintenanceHistory.length > 0) || scheduleStatus === 'done' || contractComplete) && (
        <div style={{ padding: '0 1rem 0.75rem 1rem', backgroundColor: '#f9fafb' }}>
          {maintenanceHistory && maintenanceHistory.length > 0 && (
            <>
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: 500 }}>
            Maintenance History:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {maintenanceHistory.slice(0, 5).map((hist, idx) => (
              <div
                key={idx}
                style={{
                  fontSize: '0.8rem',
                  color: '#4b5563',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#ffffff',
                  padding: '0.5rem 0.6rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}
              >
                {/* Maintenance Type Badge */}
                <span style={{ 
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '0.2rem 0.5rem',
                  borderRadius: '4px',
                  ...(() => {
                    const colors = getContractTypeColor(hist.maintenanceType);
                    return { backgroundColor: colors.bg, color: colors.text };
                  })(),
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                }}>
                  {getContractTypeLabel(hist.maintenanceType)}
                </span>
                {/* Cycle/Date Info */}
                <span style={{ fontWeight: 500, flex: 1 }}>
                  {hist.cycle || 'Maintenance'}
                </span>
                <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                  {formatDate(hist.startTerm)} - {formatDate(hist.endTerm)}
                </span>
                {/* Status Badge */}
                <span style={{ 
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  padding: '0.15rem 0.4rem',
                  borderRadius: '4px',
                  backgroundColor: hist.status === 'Past' ? '#dcfce7' : hist.status === 'Active' ? '#dbeafe' : '#fef3c7',
                  color: hist.status === 'Past' ? '#166534' : hist.status === 'Active' ? '#1e40af' : '#92400e'
                }}>
                  {hist.status === 'Past' ? '✓ Done' : hist.status}
                </span>
              </div>
            ))}
          </div>
            </>
          )}
          {scheduleStatus === 'done' && (
            <button
              onClick={() => onSchedule && onSchedule(building)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.45rem 0.85rem',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: '#2563eb',
                color: '#ffffff',
                whiteSpace: 'nowrap',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                marginTop: '0.6rem',
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
            >
              <FaPlus style={{ fontSize: '0.6rem' }} />
              Schedule Next Cycle
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Maintenance Detail Modal
//
// Exported so other tabs (the home page's "Upcoming maintenances" list)
// can render the exact same cycle-detail UI when the operator clicks a
// row instead of jumping into the schedule page first.
//
// `onSaved` (optional) fires after a successful edit so the parent can
// re-fetch / merge — it receives `({ inspection: <updates>, building:
// <updates> })`. If a parent doesn't pass it, the modal still saves to
// Back4App, it just won't refresh the surrounding lists automatically.
// Map an inspection row to the contractData index it belongs to so we
// can read / write its per-contract notes. There's no building-level
// `notes` column anymore — every note lives inside the contract entry
// it describes.
function matchContractIndex(building, inspection) {
  const cd = Array.isArray(building?.contractData) ? building.contractData : [];
  if (cd.length === 0) return -1;
  if (cd.length === 1) return 0;

  // Best signal: the /api/maintenance-schedule/buildings endpoint
  // explicitly resolves which contractData entry this row belongs to
  // and ships `contractIndex` + the row-level `contractType` alongside
  // each row. Honor those before falling back to inspection sniffing.
  if (Number.isInteger(building?.contractIndex) && building.contractIndex >= 0 && building.contractIndex < cd.length) {
    return building.contractIndex;
  }
  const rowContractType = (building?.contractType || '').toLowerCase();
  if (rowContractType) {
    const i = cd.findIndex((c) => (c?.type || '').toLowerCase() === rowContractType);
    if (i >= 0) return i;
  }

  // Inspection-driven fallbacks for callers that don't carry the row
  // metadata (e.g. ad-hoc preview). `maintenanceType` is the canonical
  // backend field; the older `Type` shim is kept for legacy callers.
  const insType = (inspection?.maintenanceType || inspection?.Type || '').toLowerCase();
  // Free-form maintenanceType strings ("fancoil & erv", "heatpump",
  // "dryer vent", etc.) — match the same keyword table the backend
  // uses so a fancoil inspection lands on the hvac-maintenance contract.
  let wanted = null;
  if (/dryer/.test(insType)) wanted = 'dryer-vent';
  else if (/fancoil|fan coil|heatpump|heat pump|hvac|erv|humidifier/.test(insType)) wanted = 'hvac-maintenance';
  if (wanted) {
    const i = cd.findIndex((c) => (c?.type || '').toLowerCase() === wanted);
    if (i >= 0) return i;
  }
  const insContractType = (inspection?.contractType || '').toLowerCase();
  if (insContractType) {
    const i = cd.findIndex((c) => (c?.type || '').toLowerCase() === insContractType);
    if (i >= 0) return i;
  }
  // Last resort — first contract on the building. Better than dropping
  // the note silently when our heuristics can't disambiguate.
  return 0;
}

export function MaintenanceModal({ inspection, building, onClose, onSaved }) {
  // Resolve the contractData index this inspection maps to so the
  // Special Notes UI can edit the matching contract's notes inline.
  const matchedContractIndex = matchContractIndex(building, inspection);
  const matchedContract = matchedContractIndex >= 0
    ? (building?.contractData?.[matchedContractIndex] || null)
    : null;
  // Inspection.notes is the server-derived per-contract notes string —
  // prefer the live contractData entry when present (so editing reflects
  // immediately), otherwise fall back to the endpoint's snapshot.
  const baseContractNotes = (matchedContract?.notes ?? inspection?.notes ?? '') || '';
  const dryerContract = /dryer/i.test(inspection?.maintenanceType || inspection?.Type || '') || building?.contractType === 'dryer-vent'
    ? scopeContract({ ...building, contractType: 'dryer-vent', contractIndex: matchedContract?.type === 'dryer-vent' ? matchedContractIndex : undefined })
    : null;

  // Seed an editable copy of every field the modal exposes. Inspection
  // fields go on `form.inspection`; building-level fields (filter info)
  // and the per-contract notes go on `form.building`. Keeping them
  // grouped means the save step can fire one PUT per resource without
  // re-sniffing which table each field belongs to.
  const seedForm = React.useCallback(() => ({
    inspection: {
      startTerm: (inspection?.startTerm || '').slice(0, 10),
      endTerm: (inspection?.endTerm || '').slice(0, 10),
      cycle: inspection?.cycle || '',
      maintenanceType: inspection?.maintenanceType || '',
      dailySchedule: inspection?.dailySchedule || '',
      pricingDetails: inspection?.pricingDetails || '',
    },
    building: {
      filterType: building?.filterType || '',
      filterSize: building?.filterSize || '',
      filterQuantity: building?.filterQuantity || '',
      contractNotes: baseContractNotes,
      jobberClientId: building?.jobberClientId || '',
      jobberPropertyId: building?.jobberPropertyId || '',
      jobberClientName: building?.jobberClientName || '',
    },
  }), [inspection, building, baseContractNotes]);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(seedForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Billing / Jobber (office-only, L2/L3). Visibility gated by role.
  const billingRole = (readMaintenanceUser()?.role) || '';
  const showBilling = canSeeBillingDetails(billingRole);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clientQuery, setClientQuery] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientSearching, setClientSearching] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState(null);

  // Is this maintenance still upcoming? Only upcoming ones get pushed to
  // Jobber (we never back-fill started/past work).
  const isUpcoming = (() => {
    const s = (inspection?.startTerm || '').slice(0, 10);
    if (!s) return false;
    const start = new Date(`${s}T00:00:00`);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return start >= today;
  })();

  // Re-seed whenever a different visit's data flows in (e.g. the user
  // closes this modal, opens a different row → same modal instance).
  useEffect(() => {
    if (!editing) {
      setForm(seedForm());
      setErr('');
    }
    // We intentionally do NOT depend on `editing` here — flipping into
    // edit mode shouldn't blow away in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection?.objectId, building?.objectId]);

  const updateInspection = (field, value) => {
    setForm((s) => ({ ...s, inspection: { ...s.inspection, [field]: value } }));
  };
  const updateBuilding = (field, value) => {
    setForm((s) => ({ ...s, building: { ...s.building, [field]: value } }));
  };

  const cancelEdit = () => {
    setForm(seedForm());
    setErr('');
    setEditing(false);
  };

  const save = async () => {
    if (saving) return;
    setErr('');
    setSaving(true);
    try {
      const inspId = inspection?.objectId;
      const bldId = building?.objectId;
      const updatedInspection = {};
      const updatedBuilding = {};

      if (inspId) {
        const i = form.inspection;
        const payload = {
          startTerm: i.startTerm || undefined,
          endTerm: i.endTerm || undefined,
          cycle: i.cycle,
          maintenanceType: i.maintenanceType,
          dailySchedule: i.dailySchedule,
          pricingDetails: i.pricingDetails,
        };
        const r = await fetch(`${API_BASE_URL}/api/maintenance-inspections/${encodeURIComponent(inspId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Saving cycle failed (${r.status})`);
        }
        Object.assign(updatedInspection, i);
      }

      if (bldId) {
        const b = form.building;
        const filterDirty = (
          b.filterType !== (building?.filterType || '') ||
          b.filterSize !== (building?.filterSize || '') ||
          b.filterQuantity !== (building?.filterQuantity || '')
        );
        const notesDirty = b.contractNotes !== baseContractNotes;
        const jobberDirty = (
          b.jobberClientId !== (building?.jobberClientId || '') ||
          b.jobberPropertyId !== (building?.jobberPropertyId || '') ||
          b.jobberClientName !== (building?.jobberClientName || '')
        );

        if (filterDirty || notesDirty || jobberDirty) {
          // Build the PUT payload. Filter fields land on the building
          // row directly (backend repacks into the consolidated
          // `filterInfo` array). Notes write into the matching
          // contractData entry so they stay alongside the contract
          // they describe — no building-level `notes` column.
          const payload = {
            filterType: b.filterType,
            filterSize: b.filterSize,
            filterQuantity: b.filterQuantity,
          };
          if (jobberDirty) {
            payload.jobberClientId = b.jobberClientId;
            payload.jobberPropertyId = b.jobberPropertyId;
            payload.jobberClientName = b.jobberClientName;
          }
          if (notesDirty && matchedContractIndex >= 0) {
            const currentCD = Array.isArray(building?.contractData) ? building.contractData : [];
            const nextCD = currentCD.map((c, idx) =>
              idx === matchedContractIndex
                ? { ...(c || {}), notes: b.contractNotes }
                : c
            );
            payload.contractData = nextCD;
          }
          const r = await fetch(`${API_BASE_URL}/api/buildings/${encodeURIComponent(bldId)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!r.ok) {
            const body = await r.json().catch(() => ({}));
            throw new Error(body.error || `Saving building fields failed (${r.status})`);
          }
          Object.assign(updatedBuilding, b);
        }
      }

      // Auto-schedule on Jobber: once a client is attached and the
      // maintenance is upcoming, saving pushes (or reschedules) the job +
      // visit automatically — no separate button. Date edits on an
      // already-scheduled cycle also re-sync via this same call.
      if (showBilling && isUpcoming && form.building.jobberClientId && inspId) {
        try {
          const sr = await fetch(
            `${API_BASE_URL}/api/maintenance-inspections/${encodeURIComponent(inspId)}/jobber-schedule`,
            { method: 'POST' },
          );
          const sd = await sr.json().catch(() => ({}));
          if (sd.ok) {
            setScheduleMsg({
              ok: true,
              text: sd.updated
                ? `Jobber visit updated${sd.jobNumber ? ` (Job #${sd.jobNumber})` : ''}.`
                : `Scheduled on Jobber${sd.jobNumber ? ` — Job #${sd.jobNumber}` : ''}.`,
              uri: sd.jobberWebUri,
            });
          } else if (sd.inProgress) {
            // Another save path (backend hook) is creating the job right
            // now — that's success, not a failure to surface.
            setScheduleMsg({ ok: true, text: 'Scheduling on Jobber…' });
          } else {
            setScheduleMsg({ ok: false, text: sd.error || 'Jobber scheduling failed.' });
          }
        } catch (e) {
          setScheduleMsg({ ok: false, text: e.message || 'Jobber scheduling failed.' });
        }
      }

      if (typeof onSaved === 'function') {
        onSaved({ inspection: updatedInspection, building: updatedBuilding });
      }
      setEditing(false);
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const doClientSearch = async () => {
    setClientSearching(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/jobber/clients?q=${encodeURIComponent(clientQuery)}`);
      const d = await r.json().catch(() => ({}));
      setClientResults(d.ok ? (d.clients || []) : []);
      if (!d.ok) setScheduleMsg({ ok: false, text: d.error || 'Client search failed.' });
    } catch (e) {
      setClientResults([]);
      setScheduleMsg({ ok: false, text: e.message || 'Client search failed.' });
    } finally {
      setClientSearching(false);
    }
  };

  const pickClient = (c) => {
    updateBuilding('jobberClientId', c.id);
    updateBuilding('jobberPropertyId', c.propertyId || '');
    updateBuilding('jobberClientName', c.name || '');
    setClientPickerOpen(false);
    setClientResults([]);
    setClientQuery('');
  };

  const clearClient = () => {
    updateBuilding('jobberClientId', '');
    updateBuilding('jobberPropertyId', '');
    updateBuilding('jobberClientName', '');
  };

  if (!inspection) return null;

  // Guard the backdrop/X close while edits are unsaved so a stray
  // mis-click can't wipe in-progress changes silently.
  const guardedClose = () => {
    if (editing && !saving) {
      const ok = window.confirm('Discard unsaved changes?');
      if (!ok) return;
    }
    onClose();
  };

  // Pull live values for display so an in-progress edit is visible even
  // when not in edit mode (e.g. immediately after Save before parent
  // hydration completes). Outside of edit mode this just mirrors the
  // original props.
  const i = editing ? form.inspection : {
    startTerm: (inspection.startTerm || '').slice(0, 10),
    endTerm: (inspection.endTerm || '').slice(0, 10),
    cycle: inspection.cycle || '',
    maintenanceType: inspection.maintenanceType || '',
    dailySchedule: inspection.dailySchedule || '',
    pricingDetails: inspection.pricingDetails || '',
  };
  const b = editing ? form.building : {
    filterType: building?.filterType || '',
    filterSize: building?.filterSize || '',
    filterQuantity: building?.filterQuantity || '',
    contractNotes: baseContractNotes,
    jobberClientId: building?.jobberClientId || '',
    jobberPropertyId: building?.jobberPropertyId || '',
    jobberClientName: building?.jobberClientName || '',
  };

  // Shared input styles so the edit-mode inputs match the cards visually.
  const fieldInput = {
    width: '100%',
    padding: '0.45rem 0.6rem',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#111827',
    fontFamily: 'inherit',
    background: '#fff',
    boxSizing: 'border-box',
  };
  const fieldTextarea = {
    ...fieldInput,
    fontWeight: 500,
    minHeight: 80,
    resize: 'vertical',
    lineHeight: 1.5,
  };
  const linkBtn = {
    background: 'transparent', border: 'none', color: '#0369a1',
    fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', padding: 0,
    textDecoration: 'underline',
  };
  const smallBtn = {
    background: '#fff', border: '1px solid #7dd3fc', color: '#0369a1',
    fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
    borderRadius: 6, padding: '0.35rem 0.6rem',
  };

  return (
    <div className="internal-schedule-modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={guardedClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '14px',
          padding: 0,
          maxWidth: 760,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            padding: '1.25rem 1.5rem 1rem',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <div style={{ minWidth: 0, paddingRight: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
              {building?.name || 'Building'}
            </h3>
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
              {building?.address || ''}
            </p>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                title="Edit dates, daily schedule, filter info, and special notes"
                style={{
                  background: '#eff6ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe',
                  borderRadius: 6,
                  padding: '0.32rem 0.7rem',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: 'inherit',
                }}
              >
                Edit
              </button>
            )}
            <button
              onClick={guardedClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.4rem',
                color: '#6b7280',
                fontSize: '1.05rem',
                borderRadius: 6,
              }}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem 1.5rem 1.5rem', overflow: 'auto' }}>
          {/* Status pill + maintenance ID on one line */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.3rem 0.7rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor:
                  inspection.status === 'Past' ? '#dcfce7'
                  : inspection.status === 'Active' ? '#dbeafe'
                  : '#fef3c7',
                color:
                  inspection.status === 'Past' ? '#166534'
                  : inspection.status === 'Active' ? '#1e40af'
                  : '#92400e',
              }}
            >
              {inspection.status === 'Past' && <FaCheckCircle style={{ fontSize: '0.7rem' }} />}
              {inspection.status === 'Active' && <FaClock style={{ fontSize: '0.7rem' }} />}
              {inspection.status === 'Upcoming' && <FaCalendarAlt style={{ fontSize: '0.7rem' }} />}
              {inspection.status}
            </div>
            {inspection.maintenanceID && (
              <span
                style={{
                  fontSize: '0.7rem',
                  color: '#1d4ed8',
                  fontFamily: 'monospace',
                  background: '#eff6ff',
                  padding: '0.25rem 0.55rem',
                  borderRadius: 6,
                  fontWeight: 600,
                }}
              >
                {inspection.maintenanceID}
              </span>
            )}
          </div>

          {/* Meta grid: 4 columns of compact fields */}
          <div className="internal-form-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: '1rem',
              marginBottom: '1.25rem',
              padding: '0.85rem 1rem',
              background: '#f9fafb',
              borderRadius: 10,
              border: '1px solid #e5e7eb',
            }}
          >
            <div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, fontWeight: 600 }}>
                Start
              </div>
              {editing ? (
                <input
                  type="date"
                  value={i.startTerm}
                  onChange={(e) => updateInspection('startTerm', e.target.value)}
                  style={fieldInput}
                />
              ) : (
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827' }}>
                  {formatDate(inspection.startTerm)}
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, fontWeight: 600 }}>
                End
              </div>
              {editing ? (
                <input
                  type="date"
                  value={i.endTerm}
                  onChange={(e) => updateInspection('endTerm', e.target.value)}
                  style={fieldInput}
                />
              ) : (
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827' }}>
                  {formatDate(inspection.endTerm)}
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, fontWeight: 600 }}>
                Cycle
              </div>
              {editing ? (
                <input
                  type="text"
                  value={i.cycle}
                  onChange={(e) => updateInspection('cycle', e.target.value)}
                  placeholder="e.g., Spring 2026"
                  style={fieldInput}
                />
              ) : (
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827' }}>
                  {inspection.cycle || '—'}
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4, fontWeight: 600 }}>
                Type
              </div>
              {editing ? (
                <input
                  type="text"
                  value={i.maintenanceType}
                  onChange={(e) => updateInspection('maintenanceType', e.target.value)}
                  placeholder="e.g., fancoil, heatpump"
                  style={fieldInput}
                />
              ) : (
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827' }}>
                  {maintenanceScopeLabel(inspection, building, getContractTypeLabel(inspection.maintenanceType))}
                </div>
              )}
            </div>
          </div>

          {dryerContract && <div style={{
            marginBottom: '1rem', border: '1px solid #e5e7eb', borderRadius: 8,
            padding: '0.7rem 0.9rem', background: '#fffbeb', color: '#374151',
          }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>CONTRACT SCOPE</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 650 }}>
              {contractScopeLabel(dryerContract)}
            </div>
            <div style={{ fontSize: '0.75rem', marginTop: 4, color: contractScopeNeedsReview(dryerContract) ? '#92400e' : '#166534' }}>
              {contractScopeNeedsReview(dryerContract) ? 'Scope needs verification' : 'Verified contract scope'}
            </div>
            {dryerContract.serviceScopeNotes && <div style={{ fontSize: '0.8rem', lineHeight: 1.5, marginTop: 6, whiteSpace: 'pre-wrap' }}>
              {dryerContract.serviceScopeNotes}
            </div>}
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 6 }}>
              Contract coverage is shown for reference. The visit’s recorded service types remain unchanged.
            </div>
          </div>}

          {/* Two-column area: daily schedule on the left, filter + notes on the right */}
          <div className="internal-form-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)',
              gap: '1rem',
            }}
          >
            {/* Daily schedule */}
            <div>
              <div style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: '0.4rem', fontWeight: 600 }}>
                Daily Schedule
              </div>
              {editing ? (
                <textarea
                  value={i.dailySchedule}
                  onChange={(e) => updateInspection('dailySchedule', e.target.value)}
                  placeholder="One line per day, e.g.&#10;Jun 1, 2026 1,2,3&#10;Jun 2, 2026 4,5,6 — Holiday"
                  style={{ ...fieldTextarea, minHeight: 140 }}
                />
              ) : (
                <div
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#374151',
                    backgroundColor: '#f3f4f6',
                    padding: '0.75rem 0.9rem',
                    borderRadius: 8,
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.55,
                    minHeight: 60,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  {inspection.dailySchedule || (
                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No daily schedule set.</span>
                  )}
                </div>
              )}
            </div>

            {/* Right column: filter info + special notes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Filter info — always show so techs know whether the
                  back-office still needs to populate it. */}
              <div
                style={{
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '0.75rem 0.9rem',
                }}
              >
                <div
                  style={{
                    fontSize: '0.7rem',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '.03em',
                    marginBottom: '0.4rem',
                    fontWeight: 600,
                  }}
                >
                  Filter info
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.6rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>Type</div>
                    {editing ? (
                      <input
                        type="text"
                        value={b.filterType}
                        onChange={(e) => updateBuilding('filterType', e.target.value)}
                        placeholder="e.g., MERV 11"
                        style={{ ...fieldInput, fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
                      />
                    ) : (
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1f2937' }}>{building?.filterType || '—'}</div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.6rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>Size</div>
                    {editing ? (
                      <input
                        type="text"
                        value={b.filterSize}
                        onChange={(e) => updateBuilding('filterSize', e.target.value)}
                        placeholder="e.g., 16x25x1"
                        style={{ ...fieldInput, fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
                      />
                    ) : (
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1f2937' }}>{building?.filterSize || '—'}</div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.6rem', color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>Per unit</div>
                    {editing ? (
                      <input
                        type="text"
                        value={b.filterQuantity}
                        onChange={(e) => updateBuilding('filterQuantity', e.target.value)}
                        placeholder="e.g., 2"
                        style={{ ...fieldInput, fontSize: '0.8rem', padding: '0.35rem 0.5rem' }}
                      />
                    ) : (
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1f2937' }}>{building?.filterQuantity || '—'}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Per-contract notes (saved into contractData[i].notes for
                  the contract this inspection maps to). */}
              <div
                style={{
                  background: baseContractNotes ? '#fef3c7' : '#f9fafb',
                  border: `1px solid ${baseContractNotes ? '#fcd34d' : '#e5e7eb'}`,
                  borderRadius: 8,
                  padding: '0.75rem 0.9rem',
                  flex: 1,
                }}
              >
                <div
                  style={{
                    fontSize: '0.7rem',
                    color: baseContractNotes ? '#92400e' : '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '.03em',
                    marginBottom: '0.35rem',
                    fontWeight: 700,
                  }}
                  title={matchedContract?.type ? `Saved into the "${matchedContract.type}" contract on this building.` : undefined}
                >
                  Notes
                  {matchedContract?.type ? (
                    <span style={{ marginLeft: 6, fontWeight: 500, color: '#6b7280', textTransform: 'none', letterSpacing: 0 }}>
                      · {matchedContract.type}
                    </span>
                  ) : null}
                </div>
                {editing && matchedContractIndex >= 0 ? (
                  <textarea
                    value={b.contractNotes}
                    onChange={(e) => updateBuilding('contractNotes', e.target.value)}
                    placeholder="Anything techs should know about this contract — access codes, parking, super contact, etc."
                    style={fieldTextarea}
                  />
                ) : (
                  <div
                    style={{
                      fontSize: '0.82rem',
                      color: baseContractNotes ? '#78350f' : '#9ca3af',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                      fontStyle: baseContractNotes ? 'normal' : 'italic',
                    }}
                  >
                    {baseContractNotes || (matchedContractIndex < 0
                      ? 'No matching contract on this building to attach notes to.'
                      : 'No notes on this contract yet.')}
                  </div>
                )}
              </div>

              {/* Billing & Jobber (office-only: L2/L3). Client link is
                  per-building; pricing is per-maintenance. */}
              {showBilling && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '0.75rem 0.9rem' }}>
                  <div style={{ fontSize: '0.7rem', color: '#075985', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: '0.5rem', fontWeight: 700 }}>
                    Billing &amp; Jobber
                  </div>

                  {/* Jobber client (per building) */}
                  <div style={{ marginBottom: '0.65rem' }}>
                    <div style={{ fontSize: '0.6rem', color: '#0369a1', textTransform: 'uppercase', marginBottom: 3 }}>Jobber client</div>
                    {editing ? (
                      <div>
                        {b.jobberClientName ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0c4a6e' }}>{b.jobberClientName}</span>
                            <button type="button" onClick={() => setClientPickerOpen(true)} style={linkBtn}>Change</button>
                            <button type="button" onClick={clearClient} style={linkBtn}>Remove</button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => setClientPickerOpen(true)} style={smallBtn}>Attach client…</button>
                        )}
                        {clientPickerOpen && (
                          <div style={{ marginTop: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <input
                                value={clientQuery}
                                onChange={(e) => setClientQuery(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doClientSearch(); } }}
                                placeholder="Search Jobber clients…"
                                style={{ ...fieldInput, fontSize: '0.8rem', padding: '0.35rem 0.5rem', fontWeight: 500 }}
                              />
                              <button type="button" onClick={doClientSearch} style={smallBtn}>{clientSearching ? '…' : 'Search'}</button>
                            </div>
                            <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
                              {clientResults.map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={() => pickClient(c)}
                                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid #f1f5f9', padding: '6px 4px', cursor: 'pointer' }}
                                >
                                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1f2937' }}>{c.name}</div>
                                  {c.propertyAddress && <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>{c.propertyAddress}</div>}
                                </button>
                              ))}
                              {!clientSearching && clientResults.length === 0 && clientQuery && (
                                <div style={{ fontSize: '0.75rem', color: '#9ca3af', padding: '4px' }}>No matches.</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: building?.jobberClientName ? '#0c4a6e' : '#9ca3af', fontStyle: building?.jobberClientName ? 'normal' : 'italic' }}>
                        {building?.jobberClientName || 'No client attached'}
                      </div>
                    )}
                  </div>

                  {/* Pricing details (per maintenance) */}
                  <div style={{ marginBottom: '0.65rem' }}>
                    <div style={{ fontSize: '0.6rem', color: '#0369a1', textTransform: 'uppercase', marginBottom: 3 }}>Pricing details</div>
                    {editing ? (
                      <textarea
                        value={i.pricingDetails}
                        onChange={(e) => updateInspection('pricingDetails', e.target.value)}
                        placeholder="Plain text — e.g. 'Flat $4,500 / cycle' or '$12/unit × 696 units'."
                        style={{ ...fieldTextarea, minHeight: 56 }}
                      />
                    ) : (
                      <div style={{ fontSize: '0.82rem', color: inspection?.pricingDetails ? '#1f2937' : '#9ca3af', whiteSpace: 'pre-wrap', fontStyle: inspection?.pricingDetails ? 'normal' : 'italic' }}>
                        {inspection?.pricingDetails || 'No pricing set'}
                      </div>
                    )}
                  </div>

                  {/* Schedule status — read mode only. Scheduling happens
                      automatically on Save (no button). */}
                  {!editing && (
                    <div>
                      {inspection?.jobberJobNumber ? (
                        <div style={{ fontSize: '0.8rem', color: '#065f46' }}>
                          ✓ On Jobber — Job #{inspection.jobberJobNumber}
                          {inspection.jobberWebUri && <> · <a href={inspection.jobberWebUri} target="_blank" rel="noreferrer" style={{ color: '#0369a1' }}>open</a></>}
                        </div>
                      ) : isUpcoming ? (
                        building?.jobberClientId ? (
                          <div style={{ fontSize: '0.78rem', color: '#0369a1' }}>Editing &amp; saving will schedule this on Jobber automatically.</div>
                        ) : (
                          <div style={{ fontSize: '0.78rem', color: '#92400e' }}>Attach a Jobber client (Edit) — saving then schedules it automatically.</div>
                        )
                      ) : (
                        <div style={{ fontSize: '0.78rem', color: '#9ca3af', fontStyle: 'italic' }}>Started/past — not pushed to Jobber.</div>
                      )}
                      {scheduleMsg && (
                        <div style={{ marginTop: 6, fontSize: '0.78rem', color: scheduleMsg.ok ? '#065f46' : '#b91c1c' }}>
                          {scheduleMsg.text}
                          {scheduleMsg.uri && <> · <a href={scheduleMsg.uri} target="_blank" rel="noreferrer" style={{ color: '#0369a1' }}>open</a></>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {editing && (
          <div
            style={{
              padding: '0.85rem 1.5rem',
              borderTop: '1px solid #e5e7eb',
              background: '#fafbfc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '0.65rem',
              flexWrap: 'wrap',
            }}
          >
            {err && (
              <div style={{ flex: 1, fontSize: '0.78rem', color: '#b91c1c', background: '#fee2e2', padding: '0.45rem 0.7rem', borderRadius: 6 }}>
                {err}
              </div>
            )}
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              style={{
                background: '#fff',
                border: '1px solid #d1d5db',
                color: '#374151',
                padding: '0.5rem 0.95rem',
                borderRadius: 6,
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{
                background: saving ? '#94a3b8' : '#1d4ed8',
                border: 'none',
                color: '#fff',
                padding: '0.5rem 1.05rem',
                borderRadius: 6,
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Pure helper — generates a 4-digit numeric building id that doesn't
// collide with anything in `existing`. Defined at module scope so we
// can both seed initial state and re-run on demand from a regenerate
// button, without re-creating it on every render.
const generateUniqueBuildingId = (existing = []) => {
  const used = new Set((existing || []).map(String));
  let id;
  let attempts = 0;
  do {
    id = String(Math.floor(1000 + Math.random() * 9000));
    attempts += 1;
  } while (used.has(id) && attempts < 100);
  return id;
};

// Aggregate the building's annual ARR + lifetime value from the per-
// contract entries the user filled in. The convention used elsewhere
// in the app (BMI dashboard, ARR growth chart, top-buildings table) is:
//   contractValue = annual recurring revenue across all contracts
//   lifetimeValue = sum of (annualValue * years) across all contracts
// so we mirror that here.
const computeContractRollups = (contractData = []) => {
  let contractValue = 0;
  let lifetimeValue = 0;
  (contractData || []).forEach((c) => {
    const annual = Number(c?.annualValue) || 0;
    const months = Number(c?.termLengthMonths) || 0;
    contractValue += annual;
    lifetimeValue += annual * (months / 12);
  });
  // Round to cents so we don't ship floating-point noise like 12345.0000004
  return {
    contractValue: Math.round(contractValue * 100) / 100,
    lifetimeValue: Math.round(lifetimeValue * 100) / 100,
  };
};

// Add Building Modal
function AddBuildingModal({ onClose, onSave, existingBuildingIds = [] }) {
  const [formData, setFormData] = useState(() => ({
    name: '',
    address: '',
    units: '',
    manager: '',
    managerEmail: '',
    photo_url: '',
    region: 'Toronto',
    status: 'Active',
    buildingId: generateUniqueBuildingId(existingBuildingIds),
  }));
  const [deviceTypes, setDeviceTypes] = useState({
    fancoil: false,
    heatpump: false,
    erv: false,
    humidifier: false,
  });
  const [contractTypes, setContractTypes] = useState({
    'hvac-maintenance': false,
    'dryer-vent': false,
    'mechanical-room': false,
  });
  const [contractData, setContractData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // User-triggered regenerate of the building id — used by the small
  // refresh button next to the (read-only) id field, so back office can
  // re-roll if there's a perceived collision without typing it manually.
  const regenerateBuildingId = () => {
    setFormData(prev => ({ ...prev, buildingId: generateUniqueBuildingId(existingBuildingIds) }));
  };

  const toggleDeviceType = (type) => {
    setDeviceTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const toggleContractType = (type) => {
    setContractTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const addContractData = () => {
    setContractData(prev => [...prev, {
      type: 'hvac-maintenance',
      signedDate: new Date().toISOString().slice(0, 10),
      expiryDate: '',
      termLengthMonths: 24,
      cyclesPerYear: 2,
      annualValue: 0,
      status: 'active',
      notes: ''
    }]);
  };

  const updateContractData = (index, field, value) => {
    setContractData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-calculate expiry date when signed date or term length changes
      if (field === 'signedDate' || field === 'termLengthMonths') {
        const signed = field === 'signedDate' ? value : updated[index].signedDate;
        const months = field === 'termLengthMonths' ? parseInt(value) : updated[index].termLengthMonths;
        if (signed && months) {
          const expiryDate = new Date(signed);
          expiryDate.setMonth(expiryDate.getMonth() + months);
          updated[index].expiryDate = expiryDate.toISOString().slice(0, 10);
        }
      }
      return updated;
    });
  };

  const removeContractData = (index) => {
    setContractData(prev => prev.filter((_, i) => i !== index));
  };

  // Live rollups so the user sees ARR + lifetime update as they edit.
  const { contractValue, lifetimeValue } = computeContractRollups(contractData);

  const handleSubmit = async () => {
    if (!formData.name || !formData.address) {
      alert('Please fill in building name and address');
      return;
    }
    if (!formData.photo_url || !formData.photo_url.trim()) {
      alert('Please add a Building Photo URL — it is required so the building shows up in dashboards with an image.');
      return;
    }
    if (!formData.buildingId || !String(formData.buildingId).trim()) {
      // Defensive — the field is read-only but if something cleared it,
      // re-roll one rather than persisting a blank id.
      formData.buildingId = generateUniqueBuildingId(existingBuildingIds);
    }

    // Build deviceType array from selections
    const selectedDeviceTypes = Object.entries(deviceTypes)
      .filter(([_, selected]) => selected)
      .map(([type]) => type);

    // Build contracts array from selections
    const selectedContracts = Object.entries(contractTypes)
      .filter(([_, selected]) => selected)
      .map(([type]) => type);

    setIsSaving(true);
    try {
      await onSave({
        ...formData,
        deviceType: selectedDeviceTypes,
        contracts: selectedContracts,
        contractData: contractData,
        // Rollups computed from contractData. We send them explicitly so
        // every consumer (dashboards, leaderboards) reads a consistent
        // number instead of each having to re-aggregate the array.
        contractValue,
        lifetimeValue,
      });
      onClose();
    } catch (err) {
      alert('Failed to save building: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="internal-schedule-modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: 650,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#111827' }}>
            <FaBuilding style={{ marginRight: '0.5rem', color: '#2563eb' }} />
            Add New Building
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', color: '#6b7280' }}>
            <FaTimes />
          </button>
        </div>

        {/* Building Info Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>Building Information</h3>
          <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>
                Building ID <span style={{ color: '#6b7280', fontWeight: 400 }}>(auto, locked)</span>
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type="text"
                  value={formData.buildingId}
                  readOnly
                  // Read-only so an accidental focus + delete can't blank
                  // the id between mount and submit (which was sending
                  // buildings up to Back4App with an empty buildingId).
                  title="Auto-generated 4-digit building ID. Click the refresh icon to roll a new one."
                  style={{ flex: 1, padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', backgroundColor: '#f3f4f6', fontFamily: 'monospace', color: '#1f2937', cursor: 'default' }}
                />
                <button
                  type="button"
                  onClick={regenerateBuildingId}
                  title="Roll a new unique 4-digit building ID"
                  style={{
                    padding: '0 0.6rem',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    color: '#6b7280',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  ⟳
                </button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Status *</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
              >
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Building Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., The Well Tower A"
                style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Address *</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="e.g., 65 Bremner Blvd"
                style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Units</label>
              <input
                type="text"
                value={formData.units}
                onChange={(e) => handleChange('units', e.target.value)}
                placeholder="e.g., 450"
                style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Region</label>
              <select
                value={formData.region}
                onChange={(e) => handleChange('region', e.target.value)}
                style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
              >
                <option value="Toronto">Toronto</option>
                <option value="Mississauga">Mississauga</option>
                <option value="Vaughan">Vaughan</option>
                <option value="Markham">Markham</option>
                <option value="Richmond Hill">Richmond Hill</option>
                <option value="Brampton">Brampton</option>
                <option value="Oakville">Oakville</option>
                <option value="Burlington">Burlington</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Property Manager</label>
              <input
                type="text"
                value={formData.manager}
                onChange={(e) => handleChange('manager', e.target.value)}
                placeholder="e.g., John Smith"
                style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Manager Email</label>
              <input
                type="email"
                value={formData.managerEmail}
                onChange={(e) => handleChange('managerEmail', e.target.value)}
                placeholder="e.g., john@management.com"
                style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>
                Building Photo URL <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="url"
                value={formData.photo_url}
                onChange={(e) => handleChange('photo_url', e.target.value)}
                placeholder="https://… (paste a publicly-accessible image URL)"
                required
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  border: `1px solid ${formData.photo_url ? '#d1d5db' : '#fecaca'}`,
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  backgroundColor: formData.photo_url ? '#ffffff' : '#fef2f2',
                }}
              />
              <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Required — dashboards and the residents' portal show this image as the building's hero photo.
              </div>
            </div>
          </div>
        </div>

        {/* Device Types Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>HVAC System Type</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {[
              { key: 'fancoil', label: 'Fan Coil' },
              { key: 'heatpump', label: 'Heat Pump' },
              { key: 'erv', label: 'ERV' },
              { key: 'humidifier', label: 'Humidifier' },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleDeviceType(key)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: deviceTypes[key] ? '2px solid #2563eb' : '1px solid #d1d5db',
                  backgroundColor: deviceTypes[key] ? '#eff6ff' : '#ffffff',
                  color: deviceTypes[key] ? '#2563eb' : '#374151',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                {deviceTypes[key] ? '✓ ' : ''}{label}
              </button>
            ))}
          </div>
        </div>

        {/* Contract Types Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>Contract Types</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {[
              { key: 'hvac-maintenance', label: 'HVAC Maintenance' },
              { key: 'dryer-vent', label: 'Dryer Vent' },
              { key: 'mechanical-room', label: 'Mechanical Room' },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => toggleContractType(key)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: contractTypes[key] ? '2px solid #7c3aed' : '1px solid #d1d5db',
                  backgroundColor: contractTypes[key] ? '#f5f3ff' : '#ffffff',
                  color: contractTypes[key] ? '#7c3aed' : '#374151',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                {contractTypes[key] ? '✓ ' : ''}{label}
              </button>
            ))}
          </div>
        </div>

        {/* Contract Data Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', margin: 0 }}>Contract Details (Optional)</h3>
            <button
              onClick={addContractData}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.4rem 0.75rem',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              <FaPlus style={{ fontSize: '0.7rem' }} /> Add Details
            </button>
          </div>

          {contractData.length === 0 ? (
            <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>
              No contract details added. You can add them now or later via "Add Contract".
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {contractData.map((contract, index) => (
                <div key={index} style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>Contract {index + 1}</span>
                    <button
                      onClick={() => removeContractData(index)}
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.8rem' }}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.2rem' }}>Type</label>
                      <select
                        value={contract.type}
                        onChange={(e) => updateContractData(index, 'type', e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                      >
                        <option value="hvac-maintenance">HVAC Maintenance</option>
                        <option value="dryer-vent">Dryer Vent</option>
                        <option value="mechanical-room">Mechanical Room</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.2rem' }}>Frequency</label>
                      <select
                        value={contract.cyclesPerYear}
                        onChange={(e) => updateContractData(index, 'cyclesPerYear', parseFloat(e.target.value))}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                      >
                        <option value={4}>4x per year (quarterly)</option>
                        <option value={3}>3x per year</option>
                        <option value={2}>2x per year</option>
                        <option value={1}>1x per year</option>
                        <option value={0.5}>1x every 2 years</option>
                        <option value={1 / 3}>1x every 3 years</option>
                        <option value={0.25}>1x every 4 years</option>
                        <option value={0.2}>1x every 5 years</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.2rem' }}>Signed Date</label>
                      <input
                        type="date"
                        value={contract.signedDate}
                        onChange={(e) => updateContractData(index, 'signedDate', e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.2rem' }}>Term Length</label>
                      <select
                        value={contract.termLengthMonths}
                        onChange={(e) => updateContractData(index, 'termLengthMonths', parseInt(e.target.value))}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                      >
                        <option value={12}>1 Year</option>
                        <option value={24}>2 Years</option>
                        <option value={36}>3 Years</option>
                        <option value={48}>4 Years</option>
                        <option value={60}>5 Years</option>
                      </select>
                    </div>
                    {/* Annual value powers the building's contractValue
                        (ARR) and lifetimeValue (annual × years). Inline
                        helper text in the row footer confirms the math
                        so the back office can sanity-check before save. */}
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.2rem' }}>Annual Value ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="100"
                        value={contract.annualValue ?? ''}
                        onChange={(e) => updateContractData(index, 'annualValue', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="e.g., 12000"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.2rem' }}>Expiry Date (auto)</label>
                      <input
                        type="date"
                        value={contract.expiryDate}
                        readOnly
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', backgroundColor: '#f3f4f6' }}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1', fontSize: '0.72rem', color: '#6b7280', background: '#f9fafb', borderRadius: 4, padding: '0.35rem 0.55rem' }}>
                      {(() => {
                        const annual = Number(contract.annualValue) || 0;
                        const years = (Number(contract.termLengthMonths) || 0) / 12;
                        const lifetime = annual * years;
                        return annual > 0 && years > 0
                          ? `Lifetime for this contract: $${annual.toLocaleString()} × ${years} yr${years === 1 ? '' : 's'} = $${lifetime.toLocaleString()}`
                          : 'Enter an annual value to see this contract\'s lifetime total.';
                      })()}
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.2rem' }}>Notes</label>
                      <input
                        type="text"
                        value={contract.notes}
                        onChange={(e) => updateContractData(index, 'notes', e.target.value)}
                        placeholder="Special instructions..."
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {contractData.length > 0 && (
            // Building-level rollup — sum of annuals = ARR, sum of
            // (annual × years) = lifetime. Reads live from the contract
            // rows above so the operator can verify before submit.
            <div
              style={{
                marginTop: '0.75rem',
                padding: '0.75rem 1rem',
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ fontSize: '0.78rem', color: '#1e3a8a', fontWeight: 600 }}>
                Auto-calculated for this building
              </div>
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 500 }}>Contract Value (annual)</div>
                  <div style={{ fontSize: '1rem', color: '#111827', fontWeight: 700 }}>
                    ${contractValue.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 500 }}>Lifetime Value</div>
                  <div style={{ fontSize: '1rem', color: '#111827', fontWeight: 700 }}>
                    ${lifetimeValue.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.6rem 1.25rem',
              backgroundColor: '#ffffff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            style={{
              padding: '0.6rem 1.25rem',
              backgroundColor: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.7 : 1,
            }}
          >
            {isSaving ? 'Saving...' : 'Save Building'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Add Contract Modal
function AddContractModal({ buildings, onClose, onSave }) {
  const [selectedObjectId, setSelectedObjectId] = useState('');
  const [contract, setContract] = useState({
    type: 'hvac-maintenance',
    signedDate: new Date().toISOString().slice(0, 10),
    expiryDate: '',
    termLengthMonths: 24,
    cyclesPerYear: 2,
    annualValue: 0,
    status: 'active',
    notes: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  const selectedBuilding = buildings.find(b => b.objectId === selectedObjectId);

  const updateContract = (field, value) => {
    setContract(prev => {
      const updated = { ...prev, [field]: value };
      // Auto-calculate expiry date
      if (field === 'signedDate' || field === 'termLengthMonths') {
        const signed = field === 'signedDate' ? value : prev.signedDate;
        const months = field === 'termLengthMonths' ? parseInt(value) : prev.termLengthMonths;
        if (signed && months) {
          const expiryDate = new Date(signed);
          expiryDate.setMonth(expiryDate.getMonth() + months);
          updated.expiryDate = expiryDate.toISOString().slice(0, 10);
        }
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!selectedObjectId) {
      alert('Please select a building');
      return;
    }
    if (!contract.signedDate) {
      alert('Please enter a signed date');
      return;
    }
    setIsSaving(true);
    try {
      await onSave(selectedObjectId, contract);
      onClose();
    } catch (err) {
      alert('Failed to save contract: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Get existing contracts for selected building
  const existingContracts = selectedBuilding?.contractData || [];

  return (
    <div className="internal-schedule-modal-backdrop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: 550,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#111827' }}>
            <FaFileContract style={{ marginRight: '0.5rem', color: '#7c3aed' }} />
            Add Contract
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', color: '#6b7280' }}>
            <FaTimes />
          </button>
        </div>

        {/* Building Selection */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#374151', marginBottom: '0.35rem' }}>Select Building *</label>
          <select
            value={selectedObjectId}
            onChange={(e) => setSelectedObjectId(e.target.value)}
            style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
          >
            <option value="">-- Select a building --</option>
            {buildings.filter(b => b.status === 'Active').map(b => (
              <option key={b.objectId} value={b.objectId}>{b.name} - {b.address}</option>
            ))}
          </select>
        </div>

        {/* Show existing contracts for selected building */}
        {selectedBuilding && existingContracts.length > 0 && (
          <div style={{ marginBottom: '1.25rem', padding: '0.75rem', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>Current Contracts:</div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {existingContracts.map((c, i) => {
                const typeColors = {
                  'hvac-maintenance': { bg: '#dbeafe', text: '#1e40af' },
                  'dryer-vent': { bg: '#fef3c7', text: '#92400e' },
                  'mechanical-room': { bg: '#dcfce7', text: '#166534' }
                };
                const typeLabels = {
                  'hvac-maintenance': 'HVAC',
                  'dryer-vent': 'Dryer Vent',
                  'mechanical-room': 'Mech Room'
                };
                const colors = typeColors[c.type] || { bg: '#f3f4f6', text: '#374151' };
                const freqLabel = getFrequencyLabel(c.cyclesPerYear);
                return (
                  <span key={i} style={{
                    padding: '0.3rem 0.6rem',
                    backgroundColor: colors.bg,
                    color: colors.text,
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}>
                    {typeLabels[c.type] || c.type} ({freqLabel})
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Contract Details */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: '0.75rem' }}>Contract Details</h3>
          <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Contract Type *</label>
              <select
                value={contract.type}
                onChange={(e) => updateContract('type', e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
              >
                <option value="hvac-maintenance">HVAC Maintenance</option>
                <option value="dryer-vent">Dryer Vent</option>
                <option value="mechanical-room">Mechanical Room</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Frequency *</label>
              <select
                value={contract.cyclesPerYear}
                onChange={(e) => updateContract('cyclesPerYear', parseFloat(e.target.value))}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
              >
                <option value={4}>4x per year (quarterly)</option>
                <option value={3}>3x per year</option>
                <option value={2}>2x per year</option>
                <option value={1}>1x per year</option>
                <option value={0.5}>1x every 2 years</option>
                <option value={1 / 3}>1x every 3 years</option>
                <option value={0.25}>1x every 4 years</option>
                <option value={0.2}>1x every 5 years</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Signed Date *</label>
              <input
                type="date"
                value={contract.signedDate}
                onChange={(e) => updateContract('signedDate', e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Term Length *</label>
              <select
                value={contract.termLengthMonths}
                onChange={(e) => updateContract('termLengthMonths', parseInt(e.target.value))}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
              >
                <option value={12}>1 Year</option>
                <option value={24}>2 Years</option>
                <option value={36}>3 Years</option>
                <option value={48}>4 Years</option>
                <option value={60}>5 Years</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Annual Value ($) *</label>
              <input
                type="number"
                min="0"
                step="100"
                value={contract.annualValue ?? ''}
                onChange={(e) => updateContract('annualValue', e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g., 12000"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Expiry Date (auto)</label>
              <input
                type="date"
                value={contract.expiryDate}
                onChange={(e) => updateContract('expiryDate', e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', backgroundColor: '#f3f4f6' }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1', fontSize: '0.72rem', color: '#1e3a8a', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4, padding: '0.5rem 0.65rem' }}>
              {(() => {
                const annual = Number(contract.annualValue) || 0;
                const years = (Number(contract.termLengthMonths) || 0) / 12;
                const lifetime = annual * years;
                return annual > 0 && years > 0
                  ? `This contract adds $${annual.toLocaleString()}/yr to the building's ARR and $${lifetime.toLocaleString()} lifetime ($${annual.toLocaleString()} × ${years} yr${years === 1 ? '' : 's'}).`
                  : 'Enter an annual value to see ARR + lifetime impact.';
              })()}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Notes</label>
              <textarea
                value={contract.notes}
                onChange={(e) => updateContract('notes', e.target.value)}
                placeholder="Special instructions, terms, etc..."
                rows={2}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', resize: 'vertical' }}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.6rem 1.25rem',
              backgroundColor: '#ffffff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            style={{
              padding: '0.6rem 1.25rem',
              backgroundColor: '#7c3aed',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.7 : 1,
            }}
          >
            {isSaving ? 'Saving...' : 'Add Contract'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Color palette for different buildings
const BUILDING_COLORS = [
  '#2563eb', // blue
  '#7c3aed', // purple
  '#db2777', // pink
  '#dc2626', // red
  '#ea580c', // orange
  '#ca8a04', // yellow
  '#16a34a', // green
  '#0d9488', // teal
  '#0891b2', // cyan
  '#4f46e5', // indigo
  '#9333ea', // violet
  '#c026d3', // fuchsia
  '#059669', // emerald
  '#0284c7', // sky
  '#7c2d12', // brown
];

export function buildCalendarInspections(buildings, buildingColorMap = {}) {
  const seen = new Map();
  const result = [];
  buildings.forEach(building => {
    building.maintenanceHistory?.forEach(hist => {
      if (!(hist.startTerm || hist.endTerm) || !hist.maintenanceID) return;
      const key = JSON.stringify([building.buildingId, hist.maintenanceID, hist.startTerm || '', hist.endTerm || '']);
      const scopeLabel = maintenanceScopeLabel(hist, building);
      if (!seen.has(key)) {
        const item = {
          building, inspection: hist, scopeLabels: [scopeLabel],
          type: hist.status === 'Past' ? 'past' : 'upcoming',
          color: buildingColorMap[building.buildingId] || '#2563eb',
        };
        seen.set(key, item);
        result.push(item);
      } else {
        // Combine display labels only. Distinct windows remain separate,
        // and each editable inspection retains its original canonical type.
        const item = seen.get(key);
        if (!item.scopeLabels.includes(scopeLabel)) item.scopeLabels.push(scopeLabel);
      }
    });
  });
  return result;
}

// Full Page Calendar View Component
function CalendarView({ buildings, onSelectInspection }) {
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState('week'); // 'month' or 'week'
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Ref to the cell whose date matches today, so we can scroll it
  // into view on initial mount. Past weeks of the current month sit
  // above today; the user shouldn't have to scroll through them.
  const todayCellRef = useRef(null);
  const didInitialScrollRef = useRef(false);

  // Listen for window resize to detect mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On first paint (and only once), scroll the today cell so it sits
  // near the top of the viewport, with ~170px of breathing room for
  // the page header, month-nav toolbar and day-of-week row.
  useEffect(() => {
    if (didInitialScrollRef.current) return;
    if (!todayCellRef.current) return;
    didInitialScrollRef.current = true;
    const top = todayCellRef.current.getBoundingClientRect().top + window.scrollY - 170;
    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }, [buildings]);

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const weekStart = useMemo(() => {
    const d = new Date(currentDate);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    return d;
  }, [currentDate]);

  // Create a color map for buildings
  const buildingColorMap = useMemo(() => {
    const map = {};
    buildings.forEach((building, index) => {
      map[building.buildingId] = BUILDING_COLORS[index % BUILDING_COLORS.length];
    });
    return map;
  }, [buildings]);

  // Merge a combined visit only when both services share the exact window.
  const allInspections = useMemo(
    () => buildCalendarInspections(buildings, buildingColorMap),
    [buildings, buildingColorMap],
  );

  // Get inspections for a specific date
  const getInspectionsForDate = (dateStr) => {
    return allInspections.filter((item) => {
      const start = item.inspection.startTerm;
      const end = item.inspection.endTerm;
      if (start && end) {
        return dateStr >= start && dateStr <= end;
      }
      if (start) return dateStr === start;
      if (end) return dateStr === end;
      return false;
    });
  };

  // Classify a given calendar day within an inspection's window so we can
  // visually mark the first day ("Start") and last day ("End") of a multi-day
  // maintenance. Middle days are treated as a faded "continuation" so the
  // start/end boundaries stand out at a glance.
  const phaseForDay = (item, dateStr) => {
    const start = (item.inspection.startTerm || '').slice(0, 10);
    const end = (item.inspection.endTerm || '').slice(0, 10);
    const isStart = !!start && dateStr === start;
    const isEnd = !!end && dateStr === end;
    if (isStart && isEnd) return { label: 'Start · End', isStart, isEnd, isMiddle: false };
    if (isStart) return { label: 'Start', isStart, isEnd: false, isMiddle: false };
    if (isEnd) return { label: 'End', isStart: false, isEnd, isMiddle: false };
    return { label: null, isStart: false, isEnd: false, isMiddle: true };
  };

  const prevPeriod = () => {
    const d = new Date(currentDate);
    if (viewMode === 'month') {
      d.setMonth(d.getMonth() - 1);
    } else {
      d.setDate(d.getDate() - 7);
    }
    setCurrentDate(d);
  };

  const nextPeriod = () => {
    const d = new Date(currentDate);
    if (viewMode === 'month') {
      d.setMonth(d.getMonth() + 1);
    } else {
      d.setDate(d.getDate() + 7);
    }
    setCurrentDate(d);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate days for the view
  const generateDays = () => {
    const days = [];
    if (viewMode === 'month') {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startPadding = firstDay.getDay();

      // Add empty cells for padding
      for (let i = 0; i < startPadding; i++) {
        days.push({ empty: true, key: `empty-${i}` });
      }

      // Add days of the month
      for (let d = 1; d <= lastDay.getDate(); d++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        days.push({
          day: d,
          dateStr,
          dayName: new Date(year, month, d).toLocaleDateString('en-US', { weekday: 'short' }),
          monthDay: new Date(year, month, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          inspections: getInspectionsForDate(dateStr),
          isToday: new Date().toISOString().slice(0, 10) === dateStr,
          key: dateStr,
        });
      }
    } else {
      // Week view
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        const dateStr = d.toISOString().slice(0, 10);
        days.push({
          day: d.getDate(),
          dateStr,
          dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
          monthDay: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          inspections: getInspectionsForDate(dateStr),
          isToday: new Date().toISOString().slice(0, 10) === dateStr,
          key: dateStr,
        });
      }
    }
    return days;
  };

  const days = generateDays();

  return (
    <div className="insuite-calendar" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Calendar Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.6rem',
          flexWrap: 'wrap',
          gap: '0.75rem',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            aria-label={`Previous ${viewMode}`}
            onClick={prevPeriod}
            style={{
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FaChevronLeft />
          </button>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, minWidth: 180, textAlign: 'center' }}>
            {viewMode === 'month' ? monthName : `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
          </h3>
          <button
            aria-label={`Next ${viewMode}`}
            onClick={nextPeriod}
            style={{
              padding: '0.5rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FaChevronRight />
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={goToToday}
            style={{
              padding: '0.4rem 0.75rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 500,
            }}
          >
            Today
          </button>
          <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
            <button
              onClick={() => setViewMode('month')}
              style={{
                padding: '0.4rem 0.75rem',
                border: 'none',
                backgroundColor: viewMode === 'month' ? '#2563eb' : '#ffffff',
                color: viewMode === 'month' ? '#ffffff' : '#374151',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('week')}
              style={{
                padding: '0.4rem 0.75rem',
                border: 'none',
                borderLeft: '1px solid #d1d5db',
                backgroundColor: viewMode === 'week' ? '#2563eb' : '#ffffff',
                color: viewMode === 'week' ? '#ffffff' : '#374151',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 500,
              }}
            >
              Week
            </button>
          </div>
        </div>
      </div>

      {/* Day Headers - hidden on mobile week view */}
      {!isMobile && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '2px',
            marginBottom: '2px',
            backgroundColor: '#e5e7eb',
            flexShrink: 0,
          }}
        >
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              style={{
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '0.7rem',
                color: '#6b7280',
                padding: '0.35rem',
                backgroundColor: '#f9fafb',
                textTransform: 'uppercase',
                letterSpacing: '.03em',
              }}
            >
              {day}
            </div>
          ))}
        </div>
      )}

      {/* Calendar Grid — rows grow to fit their tallest cell. The whole
          calendar can extend below the viewport and the page scrolls,
          so every visit chip stays fully visible. */}
      {(() => {
        const today = new Date().toISOString().slice(0, 10);
        return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(7, 1fr)',
          gap: '2px',
          backgroundColor: '#e5e7eb',
        }}
      >
        {days.map((dayInfo) => {
          if (dayInfo.empty) {
            if (isMobile) return null;
            return <div key={dayInfo.key} style={{ backgroundColor: '#f3f4f6' }} />;
          }

          const { day, dateStr, inspections = [], isToday } = dayInfo;
          const hasUpcoming = inspections.some((i) => i.type === 'upcoming');
          const hasPast = inspections.some((i) => i.type === 'past');
          // Days strictly before today get "completed" treatment: each
          // visit chip is faded with a check icon so the back office can
          // scan past the done work at a glance, à la Jobber.
          const isPastDay = dateStr < today;

          // Mobile week view: stacked vertical layout
          if (isMobile) {
            return (
              <div
                key={dayInfo.key}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: isToday ? '#fef3c7' : '#ffffff',
                  borderBottom: '1px solid #e5e7eb',
                }}
              >
                <div
                  style={{
                    fontWeight: isToday ? 700 : 600,
                    fontSize: '0.9rem',
                    color: isToday ? '#92400e' : '#374151',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <span>{dayInfo.dayName}</span>
                  <span>{dayInfo.monthDay}</span>
                  {isToday && (
                    <span style={{ 
                      fontSize: '0.65rem', 
                      padding: '0.15rem 0.4rem', 
                      backgroundColor: '#fbbf24', 
                      color: '#78350f',
                      borderRadius: '4px',
                      fontWeight: 600
                    }}>
                      TODAY
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {inspections.length === 0 && (
                    <div style={{ fontSize: '0.85rem', color: '#9ca3af', fontStyle: 'italic' }}>
                      No maintenance scheduled
                    </div>
                  )}
                  {inspections.map((item, idx) => {
                    const phase = phaseForDay(item, dateStr);
                    return (
                    <button
                      key={`${item.building.buildingId}-${item.inspection.maintenanceID}-${idx}`}
                      onClick={() => onSelectInspection(item)}
                      style={{
                        fontSize: '0.85rem',
                        padding: '0.6rem 0.75rem',
                        borderRadius: '6px',
                        backgroundColor: item.color || '#2563eb',
                        color: '#ffffff',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {item.building.name}
                        <span style={{ display: 'block', fontSize: '0.72rem', opacity: .9, marginTop: 3 }}>
                          {item.scopeLabels.filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      {phase.label && (
                        <span
                          style={{
                            fontSize: '0.6rem',
                            fontWeight: 800,
                            letterSpacing: '.03em',
                            textTransform: 'uppercase',
                            padding: '2px 6px',
                            borderRadius: 4,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            backgroundColor: 'rgba(255,255,255,0.26)',
                            color: '#ffffff',
                          }}
                        >
                          {phase.label}
                        </span>
                      )}
                    </button>
                    );
                  })}
                </div>
              </div>
            );
          }

          // Desktop/tablet view
          const cellBg = isToday ? '#eff6ff' : isPastDay ? '#fafafa' : '#ffffff';
          return (
            <div
              key={dayInfo.key}
              ref={isToday ? todayCellRef : undefined}
              style={{
                padding: '0.5rem 0.5rem 0.55rem',
                backgroundColor: cellBg,
                display: 'flex',
                flexDirection: 'column',
                minHeight: viewMode === 'week' ? 320 : 140,
              }}
            >
              {/* Date header: number on the left, visit count chip on
                  the right (mimics the Jobber-style "10 visits" badge). */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  marginBottom: '0.3rem',
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontWeight: isToday ? 700 : 600,
                    fontSize: '0.85rem',
                    color: isToday ? '#1d4ed8' : isPastDay ? '#9ca3af' : '#111827',
                    lineHeight: 1,
                  }}
                >
                  {viewMode === 'week' ? dayInfo.monthDay : day}
                </span>
                {inspections.length > 0 && (
                  <span
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: isPastDay ? '#9ca3af' : '#6b7280',
                      backgroundColor: isPastDay ? '#f3f4f6' : '#eef2ff',
                      padding: '2px 6px',
                      borderRadius: 999,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {inspections.length} {inspections.length === 1 ? 'visit' : 'visits'}
                  </span>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3px',
                }}
              >
                {inspections.map((item, idx) => {
                  const chipColor = item.color || '#2563eb';
                  const phase = phaseForDay(item, dateStr);
                  return (
                    <button
                      key={`${item.building.buildingId}-${item.inspection.maintenanceID}-${idx}`}
                      onClick={() => onSelectInspection(item)}
                      style={{
                        fontSize: '0.72rem',
                        padding: '0.3rem 0.45rem',
                        borderRadius: '4px',
                        // Past-day chips render as a flat light-gray
                        // "completed" pill (the brand color is reserved
                        // for live/upcoming work).
                        backgroundColor: isPastDay ? '#eef0f3' : chipColor,
                        color: isPastDay ? '#6b7280' : '#ffffff',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        border: isPastDay ? '1px solid #e5e7eb' : 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        width: '100%',
                        fontWeight: 500,
                        lineHeight: 1.25,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 4,
                      }}
                      title={`${item.building.name} · ${item.scopeLabels.filter(Boolean).join(' · ')}${phase.label ? ` — ${phase.label}` : ''}`}
                    >
                      {isPastDay && (
                        <FaCheckCircle
                          style={{
                            fontSize: '0.7rem',
                            marginTop: 1,
                            flexShrink: 0,
                            color: '#22c55e',
                          }}
                        />
                      )}
                      <span style={{ minWidth: 0, flex: 1 }}>
                        {item.building.name}
                        <span style={{ display: 'block', fontSize: '0.65rem', opacity: .9, marginTop: 2 }}>
                          {item.scopeLabels.filter(Boolean).join(' · ')}
                        </span>
                      </span>
                      {phase.label && (
                        <span
                          style={{
                            fontSize: '0.5rem',
                            fontWeight: 800,
                            letterSpacing: '.03em',
                            textTransform: 'uppercase',
                            padding: '1px 4px',
                            borderRadius: 3,
                            lineHeight: 1.35,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            backgroundColor: isPastDay ? '#e2e5ea' : 'rgba(255,255,255,0.26)',
                            color: isPastDay ? '#4b5563' : '#ffffff',
                          }}
                        >
                          {phase.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
        );
      })()}

    </div>
  );
}

// Schedule Maintenance Modal - for scheduling or editing a maintenance inspection.
// `proposal` lets the Seasonal Planner reuse this exact production workflow
// with its proposed dates/details prefilled.
export function ScheduleMaintenanceModal({ building, onClose, onSave, onDelete, proposal = null }) {
  // Check if we're editing an existing upcoming inspection
  const existingInspection = building.upcomingInspection;
  const isEditing = !!existingInspection;
  const dryerLabel = contractScopeLabel(scopeContract({ ...building, contractType: 'dryer-vent', contractIndex: undefined }));
  const excludedTypes = new Set(!isEditing && Array.isArray(proposal?.skippedContractTypes) ? proposal.skippedContractTypes : []);
  const isExcludedEquipment = type => excludedTypes.has(type === 'dryer vent' ? 'dryer-vent' : 'hvac-maintenance');

  // Parse existing maintenance types if editing
  const parseExistingTypes = () => {
    if (!isEditing && Array.isArray(proposal?.maintenanceTypes) && proposal.maintenanceTypes.length > 0) {
      return proposal.maintenanceTypes;
    }
    if (!existingInspection?.maintenanceType) return [];
    const typeStr = existingInspection.maintenanceType.toLowerCase();
    const types = [];
    if (typeStr.includes('fancoil') || typeStr.includes('fan coil')) types.push('fancoil');
    if (typeStr.includes('heatpump') || typeStr.includes('heat pump')) types.push('heatpump');
    if (typeStr.includes('erv')) types.push('erv');
    if (typeStr.includes('humidifier')) types.push('humidifier');
    if (typeStr.includes('dryer')) types.push('dryer vent');
    return types;
  };

  const [formData, setFormData] = useState({
    startDate: existingInspection?.startTerm || proposal?.startDate || '',
    endDate: existingInspection?.endTerm || proposal?.endDate || '',
    cycle: existingInspection?.cycle || proposal?.cycle || '',
    maintenanceTypes: parseExistingTypes(),
    status: existingInspection?.status || 'Upcoming',
    dailySchedule: existingInspection?.dailySchedule || proposal?.dailySchedule || '',
    pricingDetails: existingInspection?.pricingDetails || proposal?.pricingDetails || '',
  });
  const [filterData, setFilterData] = useState({
    filterType: proposal?.filterType ?? building?.filterType ?? '',
    filterSize: proposal?.filterSize ?? building?.filterSize ?? '',
    filterQuantity: proposal?.filterQuantity ?? building?.filterQuantity ?? '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Billing / Jobber (office-only, L2/L3). Client link is per-building.
  const billingRole = (readMaintenanceUser()?.role) || '';
  const showBilling = canSeeBillingDetails(billingRole);
  const [jobberClient, setJobberClient] = useState({
    id: building?.jobberClientId || '',
    propertyId: building?.jobberPropertyId || '',
    name: building?.jobberClientName || '',
  });
  const [scheduleMsg, setScheduleMsg] = useState(null);

  // Calculate the next maintenance ID or use existing
  const lastMaintenanceId = building.maintenanceHistory?.[0]?.maintenanceID || null;
  const getNextMaintenanceId = () => {
    if (proposal?.maintenanceId) return proposal.maintenanceId;
    if (isEditing && existingInspection?.maintenanceID) {
      return existingInspection.maintenanceID;
    }
    if (lastMaintenanceId) {
      const parts = lastMaintenanceId.split('-');
      if (parts.length === 2) {
        const buildingIdPart = parts[0];
        const sequence = parseInt(parts[1], 10) || 0;
        return `${buildingIdPart}-${sequence + 1}`;
      }
    }
    return `${building.buildingId}-1`;
  };

  const [maintenanceId, setMaintenanceId] = useState(getNextMaintenanceId());

  // Auto-suggest cycle based on date
  const suggestCycle = (dateStr) => {
    if (!dateStr) return '';
    const d = parseLocalDate(dateStr);
    if (!d || isNaN(d.getTime())) return '';
    const month = d.getMonth();
    const year = d.getFullYear();
    if (month >= 2 && month <= 5) return `Spring ${year}`;
    if (month >= 8 && month <= 11) return `Fall ${year}`;
    if (month <= 1) return `Winter ${year}`;
    return `Summer ${year}`;
  };

  const handleStartDateChange = (value) => {
    setFormData(prev => ({
      ...prev,
      startDate: value,
      cycle: prev.cycle || suggestCycle(value),
    }));
  };

  const toggleMaintenanceType = (type) => {
    if (isExcludedEquipment(type)) return;
    setFormData(prev => {
      const types = prev.maintenanceTypes.includes(type)
        ? prev.maintenanceTypes.filter(t => t !== type)
        : [...prev.maintenanceTypes, type];
      return { ...prev, maintenanceTypes: types };
    });
  };

  // Get equipment types based on contract type
  const getEquipmentOptions = () => {
    if (proposal) {
      return [
        { id: 'fancoil', label: 'Fan Coil' },
        { id: 'heatpump', label: 'Heat Pump' },
        { id: 'erv', label: 'ERV' },
        { id: 'humidifier', label: 'Humidifier' },
        { id: 'dryer vent', label: dryerLabel },
      ];
    }
    if (building.contractType === 'hvac-maintenance') {
      return [
        { id: 'fancoil', label: 'Fan Coil' },
        { id: 'heatpump', label: 'Heat Pump' },
        { id: 'erv', label: 'ERV' },
        { id: 'humidifier', label: 'Humidifier' },
      ];
    } else if (building.contractType === 'dryer-vent') {
      return [
        { id: 'dryer vent', label: dryerLabel },
      ];
    }
    return [
      { id: 'fancoil', label: 'Fan Coil' },
      { id: 'heatpump', label: 'Heat Pump' },
      { id: 'erv', label: 'ERV' },
      { id: 'dryer vent', label: dryerLabel },
    ];
  };

  const handleDelete = async () => {
    if (!existingInspection?.objectId) {
      alert('Cannot delete: inspection ID not found');
      return;
    }
    setIsDeleting(true);
    try {
      await onDelete(existingInspection.objectId);
      onClose();
    } catch (err) {
      alert('Failed to delete inspection: ' + err.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.startDate) {
      alert('Please enter a start date');
      return;
    }
    if (formData.maintenanceTypes.length === 0) {
      alert('Please select at least one equipment type');
      return;
    }
    if (formData.maintenanceTypes.some(isExcludedEquipment)) {
      alert('A selected service is skipped or already booked for this seasonal plan. Review the seasonal scope before approving.');
      return;
    }

    setIsSaving(true);
    try {
      // Persist building-level filter details and Jobber client link before
      // creating the inspection. This keeps Seasonal Planner approval and
      // the standard Schedule modal on the same canonical Building fields.
      const buildingUpdates = {};
      if (
        filterData.filterType !== (building?.filterType || '')
        || filterData.filterSize !== (building?.filterSize || '')
        || filterData.filterQuantity !== (building?.filterQuantity || '')
      ) {
        buildingUpdates.filterType = filterData.filterType;
        buildingUpdates.filterSize = filterData.filterSize;
        buildingUpdates.filterQuantity = filterData.filterQuantity;
      }
      if (showBilling && (
        jobberClient.id !== (building?.jobberClientId || '') ||
        jobberClient.propertyId !== (building?.jobberPropertyId || '') ||
        jobberClient.name !== (building?.jobberClientName || '')
      )) {
        buildingUpdates.jobberClientId = jobberClient.id;
        buildingUpdates.jobberPropertyId = jobberClient.propertyId;
        buildingUpdates.jobberClientName = jobberClient.name;
      }
      if (building?.objectId && Object.keys(buildingUpdates).length > 0) {
        try {
          const buildingResponse = await fetch(`${API_BASE_URL}/api/buildings/${encodeURIComponent(building.objectId)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(buildingUpdates),
          });
          if (!buildingResponse.ok) throw new Error('Could not save building details.');
        } catch (e) {
          throw new Error(e.message || 'Could not save building details.');
        }
      }

      const maintenanceTypeStr = formData.maintenanceTypes.join(', ');
      const inspectionData = {
        buildingId: building.buildingId,
        maintenanceID: maintenanceId,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        startTerm: formData.startDate,
        endTerm: formData.endDate || formData.startDate,
        cycle: formData.cycle,
        maintenanceType: maintenanceTypeStr,
        Type: proposal?.seasonalApproval && !isEditing
          ? (formData.maintenanceTypes.every(type => type === 'dryer vent') ? 'dryer' : 'hvac')
          : (building.contractType === 'dryer-vent' ? 'dryer' : 'hvac'),
        status: formData.status,
        dailySchedule: formData.dailySchedule,
        pricingDetails: formData.pricingDetails,
        dataFormatVersion: 'new',
        // Include objectId if editing
        ...(isEditing && existingInspection?.objectId ? { objectId: existingInspection.objectId } : {}),
      };
      const savedId = await onSave(inspectionData, isEditing);

      // Auto-schedule on Jobber: upcoming maintenance + client attached →
      // push (or reschedule) the job + visit automatically (assigned Brian).
      const startStr = formData.startDate;
      const todayStr = new Date().toISOString().slice(0, 10);
      const isUpcoming = startStr && startStr >= todayStr;
      // Seasonal creation defers the server's automatic sync until onSave
      // confirms the plan link. Preserve sync for existing linked clients
      // even when the employee cannot see billing controls.
      if ((showBilling || proposal?.seasonalApproval) && jobberClient.id && savedId && isUpcoming) {
        try {
          const sr = await fetch(
            `${API_BASE_URL}/api/maintenance-inspections/${encodeURIComponent(savedId)}/jobber-schedule`,
            { method: 'POST' },
          );
          const sd = await sr.json().catch(() => ({}));
          // inProgress = the backend save-hook is already creating the job;
          // that's success from the office's point of view.
          if (!sd.ok && !sd.inProgress) {
            setScheduleMsg({ ok: false, text: `Saved, but Jobber scheduling failed: ${sd.error || 'unknown error'}` });
            setIsSaving(false);
            return; // keep modal open so the office sees the failure
          }
        } catch (e) {
          setScheduleMsg({ ok: false, text: `Saved, but Jobber scheduling failed: ${e.message || 'network error'}` });
          setIsSaving(false);
          return;
        }
      }
      onClose();
    } catch (err) {
      alert('Failed to schedule maintenance: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const equipmentOptions = getEquipmentOptions();

  return (
    <div className="internal-schedule-modal-backdrop" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>
              {isEditing ? 'Edit Scheduled Maintenance' : proposal ? 'Approve Schedule' : 'Schedule Maintenance'}
            </h2>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
              {building.name} • {contractScopeLabel(building, getContractTypeLabel(building.contractType))}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: '#9ca3af',
            }}
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1rem 1.25rem' }}>
          {/* Building Equipment Info */}
          {building.deviceType && building.deviceType.length > 0 && (
            <div style={{
              backgroundColor: '#ecfdf5',
              padding: '0.4rem 0.6rem',
              borderRadius: '6px',
              marginBottom: '0.75rem',
              fontSize: '0.7rem',
              color: '#047857',
            }}>
              <span style={{ fontWeight: 600 }}>Building Equipment:</span>{' '}
              {building.deviceType.map(d => {
                const labels = {
                  fancoil: 'Fan Coil',
                  heatpump: 'Heat Pump',
                  erv: 'ERV',
                  humidifier: 'Humidifier',
                };
                return labels[d] || d;
              }).join(', ')}
            </div>
          )}

          {/* Maintenance ID - Editable */}
          <div style={{
            backgroundColor: '#f3f4f6',
            padding: '0.5rem 0.75rem',
            borderRadius: '8px',
            marginBottom: '0.75rem',
          }}>
            <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.35rem' }}>
              MAINTENANCE ID
            </div>
            <input
              type="text"
              value={maintenanceId}
              onChange={(e) => setMaintenanceId(e.target.value)}
              style={{
                width: '100%',
                padding: '0.4rem 0.5rem',
                fontSize: '1rem',
                fontWeight: 700,
                color: '#111827',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: '#ffffff',
              }}
            />
            {lastMaintenanceId && !isEditing && (
              <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.35rem' }}>
                Last: {lastMaintenanceId}
              </div>
            )}
            {isEditing && (
              <div style={{ fontSize: '0.65rem', color: '#2563eb', marginTop: '0.35rem' }}>
                ✎ Editing existing inspection
              </div>
            )}
          </div>

          {/* Equipment Types */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.35rem' }}>
              Equipment Type(s) *
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {equipmentOptions.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => toggleMaintenanceType(opt.id)}
                  disabled={isExcludedEquipment(opt.id)}
                  title={isExcludedEquipment(opt.id) ? 'Skipped or already booked/completed for this seasonal plan' : undefined}
                  style={{
                    padding: '0.35rem 0.75rem',
                    borderRadius: '6px',
                    border: formData.maintenanceTypes.includes(opt.id) ? '2px solid #2563eb' : '1px solid #d1d5db',
                    backgroundColor: formData.maintenanceTypes.includes(opt.id) ? '#dbeafe' : '#ffffff',
                    color: formData.maintenanceTypes.includes(opt.id) ? '#1e40af' : '#374151',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    cursor: isExcludedEquipment(opt.id) ? 'not-allowed' : 'pointer',
                    opacity: isExcludedEquipment(opt.id) ? .45 : 1,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {excludedTypes.size > 0 && <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: 6 }}>
              Skipped and already booked services are unavailable for this approval. Change the seasonal plan to revise the selection.
            </div>}
          </div>

          {/* Dates */}
          <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.35rem' }}>
                Start Date *
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.35rem' }}>
                End Date
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.9rem',
                }}
              />
            </div>
          </div>

          {/* Cycle Name & Status - inline */}
          <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.25rem' }}>
                Cycle Name
              </label>
              <input
                type="text"
                placeholder="e.g., Spring 2026"
                value={formData.cycle}
                onChange={(e) => setFormData(prev => ({ ...prev, cycle: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.4rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.85rem',
                }}
              />
            </div>

            {/* Status */}
            <div>
              <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.25rem' }}>
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.4rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.85rem',
                }}
              >
                <option value="Upcoming">Upcoming</option>
                <option value="Active">Active (In Progress)</option>
              </select>
            </div>
          </div>

          {/* Canonical Building filter fields — same three-field format used
              in Manage Accounts and maintenance details. */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ fontSize: '0.7rem', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '0.35rem' }}>
              Filter Info
            </label>
            <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr .8fr', gap: '0.5rem' }}>
              {[
                ['filterType', 'Type', 'e.g. MERV 11'],
                ['filterSize', 'Size', 'e.g. 16x25x1'],
                ['filterQuantity', 'Per unit', 'e.g. 2'],
              ].map(([key, label, placeholder]) => (
                <label key={key}>
                  <span style={{ display: 'block', fontSize: '0.62rem', color: '#6b7280', textTransform: 'uppercase', marginBottom: 3 }}>{label}</span>
                  <input
                    type="text"
                    value={filterData[key]}
                    onChange={(e) => setFilterData((current) => ({ ...current, [key]: e.target.value }))}
                    placeholder={placeholder}
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '0.4rem',
                      borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem',
                    }}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Daily Schedule */}
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.25rem' }}>
              Daily Schedule
            </label>
            <textarea
              placeholder="e.g., Day 1: Floors 1-5, Day 2: Floors 6-10..."
              value={formData.dailySchedule}
              onChange={(e) => setFormData(prev => ({ ...prev, dailySchedule: e.target.value }))}
              rows={3}
              style={{
                width: '100%',
                padding: '0.4rem',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                resize: 'vertical',
              }}
            />
          </div>

          {/* Billing & Jobber — office-only (L2/L3). Client link is
              per-building; pricing is per-maintenance. */}
          {showBilling && (
            <div style={{ marginTop: '0.25rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '0.75rem 0.9rem' }}>
              <div style={{ fontSize: '0.7rem', color: '#075985', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: '0.5rem', fontWeight: 700 }}>
                Billing &amp; Jobber
              </div>
              <div style={{ marginBottom: '0.6rem' }}>
                <label style={{ fontSize: '0.62rem', color: '#0369a1', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Jobber client (this building)</label>
                <JobberClientPicker
                  clientName={jobberClient.name}
                  onPick={(c) => setJobberClient({ id: c.id, propertyId: c.propertyId || '', name: c.name || '' })}
                  onClear={() => setJobberClient({ id: '', propertyId: '', name: '' })}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.62rem', color: '#0369a1', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Pricing details</label>
                <textarea
                  placeholder="Plain text — e.g. 'Flat $4,500 / cycle' or '$12/unit × 696 units'."
                  value={formData.pricingDetails}
                  onChange={(e) => setFormData(prev => ({ ...prev, pricingDetails: e.target.value }))}
                  rows={2}
                  style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.8rem', fontFamily: 'inherit', resize: 'vertical' }}
                />
                <div style={{ fontSize: '0.7rem', color: '#0369a1', marginTop: 4 }}>
                  With a client attached, saving an upcoming maintenance schedules it on Jobber automatically (assigned to Brian).
                </div>
                {scheduleMsg && (
                  <div style={{ marginTop: 6, fontSize: '0.75rem', color: scheduleMsg.ok ? '#065f46' : '#b91c1c' }}>{scheduleMsg.text}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div style={{
            padding: '1rem 1.5rem',
            backgroundColor: '#fef2f2',
            borderTop: '1px solid #fecaca',
          }}>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#991b1b' }}>
              Are you sure you want to delete this scheduled maintenance? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#dc2626',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  opacity: isDeleting ? 0.6 : 1,
                }}
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                No, Keep It
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          {/* Delete button - only show when editing */}
          {isEditing ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                padding: '0.6rem 1rem',
                borderRadius: '6px',
                border: '1px solid #fecaca',
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                fontWeight: 500,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              Delete
            </button>
          ) : (
            <div />
          )}
          
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.6rem 1.25rem',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: '#ffffff',
                color: '#374151',
                fontWeight: 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              style={{
                padding: '0.6rem 1.25rem',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.6 : 1,
              }}
            >
              {isSaving ? 'Saving...' : (isEditing ? 'Save Changes' : proposal ? 'Approve Schedule' : 'Schedule Maintenance')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MaintenanceSchedule({ initialTab = 'list', embedded = false } = {}) {
  const [buildings, setBuildings] = useState([]);
  const [allBuildings, setAllBuildings] = useState([]); // For the contract modal dropdown
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState({});
  // `initialTab` lets the new /maintenance hub deep-link to list vs.
  // calendar without users having to manually click the internal toggle.
  // Defaults to 'list' so all existing callers (e.g. /maintenance-schedule)
  // behave exactly as before.
  //
  // `embedded` hides chrome the hub already provides: the "back to /schedule"
  // arrow (the hub's sidebar is the navigation now) and the internal
  // List/Calendar toggle (each is its own sidebar tab). When embedded and on
  // the calendar tab we also drop the stats pills, since the user asked for
  // a clean calendar-only view there.
  const [activeTab, setActiveTab] = useState(initialTab); // 'list' or 'calendar'
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [showAddBuildingModal, setShowAddBuildingModal] = useState(false);
  const [showAddContractModal, setShowAddContractModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingBuilding, setSchedulingBuilding] = useState(null);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load maintenance schedule buildings
      const resp = await fetch(`${API_BASE_URL}/api/maintenance-schedule/buildings`);
      if (!resp.ok) {
        throw new Error(`Failed to load buildings: ${resp.status}`);
      }
      const data = await resp.json();
      setBuildings(Array.isArray(data.buildings) ? data.buildings : []);
      
      // Also load all buildings for the contract modal dropdown
      const allBuildingsResp = await fetch(`${API_BASE_URL}/api/buildings`);
      if (allBuildingsResp.ok) {
        const allData = await allBuildingsResp.json();
        setAllBuildings(Array.isArray(allData.buildings) ? allData.buildings : []);
      }
      
      setError(null);
    } catch (e) {
      console.error('[MaintenanceSchedule] Failed to fetch buildings:', e);
      setBuildings([]);
      setError('Failed to load maintenance schedule. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Save new building
  const handleSaveBuilding = async (buildingData) => {
    const resp = await fetch(`${API_BASE_URL}/api/buildings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildingData),
    });
    if (!resp.ok) {
      throw new Error('Failed to save building');
    }
    // Reload data
    await loadData();
  };

  // Save new contract to existing building. We also recompute the
  // building's contractValue / lifetimeValue from the post-merge
  // contractData array so the dashboards immediately see the new ARR
  // / lifetime totals (the backend recomputes too, but sending them
  // explicitly avoids any 'stale until reload' surprises).
  const handleSaveContract = async (objectId, contractData) => {
    const building = allBuildings.find(b => b.objectId === objectId);
    if (!building) {
      throw new Error('Building not found');
    }
    const existingContracts = building.contractData || [];
    const updatedContracts = [...existingContracts, contractData];
    const { contractValue, lifetimeValue } = computeContractRollups(updatedContracts);

    const resp = await fetch(`${API_BASE_URL}/api/buildings/${objectId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contractData: updatedContracts,
        contractValue,
        lifetimeValue,
      }),
    });
    if (!resp.ok) {
      throw new Error('Failed to save contract');
    }
    await loadData();
  };

  const toggleHistory = (buildingId) => {
    setExpandedHistory((prev) => ({
      ...prev,
      [buildingId]: !prev[buildingId],
    }));
  };

  const handleSelectInspection = ({ building, inspection }) => {
    setSelectedBuilding(building);
    setSelectedInspection(inspection);
  };

  const closeModal = () => {
    setSelectedInspection(null);
    setSelectedBuilding(null);
  };

  // Open schedule modal for a building
  const handleOpenScheduleModal = (building) => {
    setSchedulingBuilding(building);
    setShowScheduleModal(true);
  };

  // Schedule or update a maintenance inspection
  const handleScheduleMaintenance = async (inspectionData, isEditing = false) => {
    const url = isEditing && inspectionData.objectId
      ? `${API_BASE_URL}/api/maintenance-inspections/${inspectionData.objectId}`
      : `${API_BASE_URL}/api/maintenance-inspections`;
    
    const resp = await fetch(url, {
      method: isEditing ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(inspectionData),
    });
    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({}));
      throw new Error(errData.error || `Failed to ${isEditing ? 'update' : 'schedule'} maintenance`);
    }
    const body = await resp.json().catch(() => ({}));
    const savedId = inspectionData.objectId || body.objectId || '';
    // Reload data to show the changes
    await loadData();
    return savedId;
  };

  // Delete a maintenance inspection
  const handleDeleteInspection = async (objectId) => {
    const resp = await fetch(`${API_BASE_URL}/api/maintenance-inspections/${objectId}`, {
      method: 'DELETE',
    });
    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({}));
      throw new Error(errData.error || 'Failed to delete inspection');
    }
    // Reload data
    await loadData();
  };

  // Group buildings by status
  const needsScheduling = useMemo(
    () => buildings.filter((b) => b.scheduleStatus === 'needs_scheduling'),
    [buildings]
  );
  const upcoming = useMemo(() => {
    const list = buildings.filter((b) => b.scheduleStatus === 'upcoming');
    return [...list].sort((a, b) => {
      const startA = a.upcomingInspection?.startTerm || '';
      const startB = b.upcomingInspection?.startTerm || '';
      return startA.localeCompare(startB);
    });
  }, [buildings]);
  const done = useMemo(() => buildings.filter((b) => b.scheduleStatus === 'done'), [buildings]);
  const renewals = useMemo(
    () => buildings
      .filter((b) => b.scheduleStatus === 'contract_complete' || b.contractObligationComplete)
      .sort((a, b) => (a.expiryDate || '9999-12-31').localeCompare(b.expiryDate || '9999-12-31')),
    [buildings]
  );

  return (
    <div className="maintenance-schedule-page" style={{
      padding: activeTab === 'calendar' ? '1rem' : '1.5rem 2rem', 
      backgroundColor: '#f9fafb', 
      minHeight: '100vh',
      overflowY: 'auto',
      overflowX: 'hidden',
    }}>
      <div style={{ 
        maxWidth: activeTab === 'calendar' ? 'none' : 900, 
        margin: '0 auto',
        width: '100%',
        paddingBottom: '2rem',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {!embedded && (
                <Link
                  to="/schedule"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: '8px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    textDecoration: 'none',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                >
                  <FaArrowLeft />
                </Link>
              )}
              <h1
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: '#111827',
                  margin: 0,
                }}
              >
                {embedded && activeTab === 'calendar' ? 'Calendar' : 'Maintenance Schedule'}
              </h1>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {/* Admin Mode Toggle — hidden on the embedded calendar
                  tab to keep the calendar header uncluttered. Admin
                  actions live on the Schedule and Manage Accounts
                  tabs instead. */}
              {(embedded && activeTab === 'calendar') ? null : !isAdminMode ? (
                <button
                  onClick={() => setShowPasswordPrompt(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    padding: '0.4rem 0.75rem',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  <FaLock style={{ fontSize: '0.65rem' }} />
                  Admin
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setShowAddContractModal(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.5rem 0.85rem',
                      backgroundColor: '#7c3aed',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <FaFileContract style={{ fontSize: '0.8rem' }} />
                    Add Contract
                  </button>
                  <button
                    onClick={() => setShowAddBuildingModal(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.5rem 0.85rem',
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <FaPlus style={{ fontSize: '0.75rem' }} />
                    Add Building
                  </button>
                  <button
                    onClick={() => setIsAdminMode(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      padding: '0.4rem 0.6rem',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <FaTimes style={{ fontSize: '0.6rem' }} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats — hidden on the embedded calendar tab so the calendar
            view is uncluttered (the Home tab carries the headline numbers). */}
        {!(embedded && activeTab === 'calendar') && (() => {
          return (
            <div className="schedule-summary"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: '0.6rem',
                marginBottom: '1rem',
              }}
            >
              <div
                style={{
                  backgroundColor: '#fef2f2',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  border: '1px solid #fecaca',
                }}
              >
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#dc2626' }}>
                  {needsScheduling.length}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#991b1b', fontWeight: 500 }}>
                  Need Scheduling
                </div>
              </div>
              <div
                style={{
                  backgroundColor: '#eff6ff',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  border: '1px solid #bfdbfe',
                }}
              >
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2563eb' }}>
                  {upcoming.length}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#1e40af', fontWeight: 500 }}>
                  Scheduled
                </div>
              </div>
              <div
                style={{
                  backgroundColor: '#f0fdf4',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  border: '1px solid #bbf7d0',
                }}
              >
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#16a34a' }}>
                  {done.length}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 500 }}>
                  Up to Date
                </div>
              </div>
              <div
                style={{
                  backgroundColor: '#fef3c7',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  border: '1px solid #fcd34d',
                }}
              >
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#d97706' }}>
                  {renewals.length}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 500 }}>
                  Renewal Due
                </div>
              </div>
              <div
                style={{
                  backgroundColor: '#f9fafb',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#374151' }}>
                  {buildings.length}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>
                  Total Contracts
                </div>
              </div>
            </div>
          );
        })()}

        {/* Tab Buttons — hidden when embedded since the hub sidebar already
            distinguishes Schedule vs Calendar. */}
        {!embedded && (
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            <button
              onClick={() => setActiveTab('list')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: activeTab === 'list' ? '2px solid #2563eb' : '1px solid #d1d5db',
                backgroundColor: activeTab === 'list' ? '#eff6ff' : '#ffffff',
                color: activeTab === 'list' ? '#2563eb' : '#374151',
                fontWeight: 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              <FaList /> List View
            </button>
            <button
              onClick={() => setActiveTab('calendar')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: activeTab === 'calendar' ? '2px solid #2563eb' : '1px solid #d1d5db',
                backgroundColor: activeTab === 'calendar' ? '#eff6ff' : '#ffffff',
                color: activeTab === 'calendar' ? '#2563eb' : '#374151',
                fontWeight: 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              <FaCalendarAlt /> Calendar
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4rem 1rem',
            }}
          >
            <Spinner animation="border" role="status" style={{ color: '#2563eb', marginBottom: '1rem' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>Loading...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#991b1b',
              fontSize: '0.9rem',
            }}
          >
            {error}
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && (
          <>
            {activeTab === 'list' && (
              <div>
                {/* Needs Scheduling Section */}
                {needsScheduling.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem',
                      }}
                    >
                      <FaExclamationTriangle style={{ color: '#dc2626' }} />
                      <h2
                        style={{
                          margin: 0,
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#dc2626',
                        }}
                      >
                        Needs Scheduling ({needsScheduling.length})
                      </h2>
                    </div>
                    {/* Column Headers */}
                    <div className="schedule-contract-head"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(220px, 2fr) 80px 100px 100px 130px auto',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.5rem 1rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        marginBottom: '0.5rem',
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      <div>Building / Contract</div>
                      <div style={{ textAlign: 'center' }}>Term</div>
                      <div style={{ textAlign: 'center' }}>Contract</div>
                      <div style={{ textAlign: 'center' }}>Last 12 Mo</div>
                      <div style={{ textAlign: 'center' }}>Status</div>
                      <div></div>
                    </div>
                    {needsScheduling.map((building) => (
                      <ContractRow
                        key={`${building.buildingId}-${building.contractType}`}
                        building={building}
                        showHistory={expandedHistory[`${building.buildingId}-${building.contractType}`]}
                        onToggleHistory={toggleHistory}
                        onSchedule={handleOpenScheduleModal}
                      />
                    ))}
                  </div>
                )}

                {/* Upcoming Section */}
                {upcoming.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem',
                      }}
                    >
                      <FaClock style={{ color: '#2563eb' }} />
                      <h2
                        style={{
                          margin: 0,
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#2563eb',
                        }}
                      >
                        Upcoming ({upcoming.length})
                      </h2>
                    </div>
                    {upcoming.map((building) => (
                      <ContractRow
                        key={`${building.buildingId}-${building.contractType}`}
                        building={building}
                        showHistory={expandedHistory[`${building.buildingId}-${building.contractType}`]}
                        onToggleHistory={toggleHistory}
                        onSchedule={handleOpenScheduleModal}
                      />
                    ))}
                  </div>
                )}

                {/* Contract fulfilled / renewal list */}
                {renewals.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem',
                      }}
                    >
                      <FaFileContract style={{ color: '#d97706' }} />
                      <h2
                        style={{
                          margin: 0,
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#b45309',
                        }}
                      >
                        Contract Fulfilled — Renewal List ({renewals.length})
                      </h2>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#92400e', marginBottom: '0.65rem' }}>
                      All promised cycles are complete. These contracts stay here for renewal follow-up and will not be scheduled again unless renewed.
                    </div>
                    {renewals.map((building) => (
                      <ContractRow
                        key={`${building.buildingId}-${building.contractType}`}
                        building={building}
                        showHistory={expandedHistory[`${building.buildingId}-${building.contractType}`]}
                        onToggleHistory={toggleHistory}
                        onSchedule={handleOpenScheduleModal}
                      />
                    ))}
                  </div>
                )}

                {/* Completed Section */}
                {done.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem',
                      }}
                    >
                      <FaCheckCircle style={{ color: '#16a34a' }} />
                      <h2
                        style={{
                          margin: 0,
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: '#16a34a',
                        }}
                      >
                        Up to Date — Contract Still Active ({done.length})
                      </h2>
                    </div>
                    {done.map((building) => (
                      <ContractRow
                        key={`${building.buildingId}-${building.contractType}`}
                        building={building}
                        showHistory={expandedHistory[`${building.buildingId}-${building.contractType}`]}
                        onToggleHistory={toggleHistory}
                        onSchedule={handleOpenScheduleModal}
                      />
                    ))}
                  </div>
                )}

                {/* Empty State */}
                {buildings.length === 0 && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '3rem 1rem',
                      color: '#6b7280',
                    }}
                  >
                    No buildings with active contracts found.
                  </div>
                )}
              </div>
            )}

            {activeTab === 'calendar' && (
              <div
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '0.6rem 0.6rem 0.4rem',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <CalendarView buildings={buildings} onSelectInspection={handleSelectInspection} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal for inspection details */}
      {selectedInspection && (
        <MaintenanceModal
          inspection={selectedInspection}
          building={selectedBuilding}
          onClose={closeModal}
          // Refresh the buildings list (which includes maintenanceHistory)
          // after a save so the row in the schedule reflects the edit
          // immediately — without this the user would see stale dates /
          // notes until they navigated away and back.
          onSaved={() => { loadData(); }}
        />
      )}

      {/* Add Building Modal */}
      {showAddBuildingModal && (
        <AddBuildingModal
          onClose={() => setShowAddBuildingModal(false)}
          onSave={handleSaveBuilding}
          existingBuildingIds={allBuildings.map(b => b.buildingId).filter(Boolean)}
        />
      )}

      {/* Add Contract Modal */}
      {showAddContractModal && (
        <AddContractModal
          buildings={allBuildings}
          onClose={() => setShowAddContractModal(false)}
          onSave={handleSaveContract}
        />
      )}

      {/* Schedule Maintenance Modal */}
      {showScheduleModal && schedulingBuilding && (
        <ScheduleMaintenanceModal
          building={schedulingBuilding}
          onClose={() => {
            setShowScheduleModal(false);
            setSchedulingBuilding(null);
          }}
          onSave={handleScheduleMaintenance}
          onDelete={handleDeleteInspection}
        />
      )}

      {/* Admin Password Prompt */}
      {showPasswordPrompt && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => {
            setShowPasswordPrompt(false);
            setPasswordInput('');
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              padding: '1.5rem',
              width: '100%',
              maxWidth: '320px',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <FaLock style={{ color: '#6b7280' }} />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827' }}>
                Admin Access
              </h3>
            </div>
            <input
              type="password"
              placeholder="Enter password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (passwordInput === 'maintenance') {
                    setIsAdminMode(true);
                    setShowPasswordPrompt(false);
                    setPasswordInput('');
                  } else {
                    alert('Incorrect password');
                    setPasswordInput('');
                  }
                }
              }}
              autoFocus
              style={{
                width: '100%',
                padding: '0.6rem 0.75rem',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '0.9rem',
                marginBottom: '1rem',
              }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowPasswordPrompt(false);
                  setPasswordInput('');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (passwordInput === 'maintenance') {
                    setIsAdminMode(true);
                    setShowPasswordPrompt(false);
                    setPasswordInput('');
                  } else {
                    alert('Incorrect password');
                    setPasswordInput('');
                  }
                }}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Unlock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
